import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { API } from "../config/api";
import { errorMessage } from "../utils/errorMessage";
import { getUserRole, ROLES, FEE_EARNERS } from "../config/roleConfig";
import { useSmartSearchResolver, refMatchesQuery } from "../hooks/useSmartSearchResolver";
import { usePipelineCacheInvalidation } from "../hooks/usePipelineCacheInvalidation";

// Reverse map: full name (lowercase) → initials, for fee earner filtering
// FEE_EARNERS items have {name, initials} — no is_active property (already pre-filtered)
const NAME_TO_INITIALS = Object.fromEntries(
  FEE_EARNERS.map(s => [s.name.toLowerCase(), s.initials])
);
import { toast } from "sonner";
import MoneyBar from "./MoneyBar";
import CreditChip from "./CreditChip";
import MatterAuditAccordion from "./MatterAuditAccordion";
import ReadReceipts from "./ReadReceipts";
import EmailDeliveryAudit from "./EmailDeliveryAudit";
import LawbillitySyncStatus from "./LawbillitySyncStatus";
import { LinkOBButton } from "./LinkOBModal";
import { LinkAMLButton, RefreshAMLInheritanceButton } from "./LinkAMLModal";
import CorrectClientDetailsButton from "./CorrectClientDetailsButton";
import { parseBlobError } from "../utils/parseBlobError";
import { obSendWithheldReason } from "../utils/obSendGuard";
import { openMatterCcl } from "../utils/openMatterCcl";
import { openConflictCheck } from "../utils/openConflictCheck";
import {
  PRE_CM_CONFLICT_NOTE, PRE_CM_STEP_FOR_STAGE, folderState, remediationLink,
} from "../utils/preCmRemediation";
import { usePreCmFolderState } from "../hooks/usePreCmFolderState";
import NextStepBanner from "./NextStepBanner";
import PipelineStageTrack from "./PipelineStageTrack";

// ─── helpers ──────────────────────────────────────────────────────────────────
const getHeaders = () => {
  const uid =
    sessionStorage.getItem("bonsai_user_id") ||
    localStorage.getItem("bonsai_user_id") ||
    "";
  return uid ? { "X-Bonsai-User-Id": uid } : {};
};

const downloadPipelineDoc = async (matterRef, docType, setBusy) => {
  if (setBusy) setBusy(true);
  try {
    const res = await axios.get(
      `${API}/pipeline/matter/${matterRef}/download/${docType}`,
      { headers: getHeaders(), responseType: "blob" }
    );
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement("a");
    a.href = url;
    // Brief 63 (23 Jun 2026): CCL download is now .docx. Other doc types
    // remain PDF. The server Content-Disposition header is authoritative;
    // this attribute is the browser fallback filename.
    // BM-H3905: the FORF and Risk Assessment are PDFs, as filed.
    const ext = docType === "ccl" ? "docx" : "pdf";
    a.setAttribute("download", `BonsaiLaw_${docType}_${matterRef}.${ext}`);
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  } catch (e) {
    const detail = await parseBlobError(e);
    alert(`Download failed: ${detail}`);
  } finally {
    if (setBusy) setBusy(false);
  }
};

// 7-stage pre-open workflow.
// 26 May 2026 (Liezel + agent): aml_check restored between ob_returned and
// forf_draft. The stage was inadvertently dropped in PR #187 (13 Apr 2026)
// when the static HomeScreen was replaced with the dynamic homepage-config
// system. Without this card the team cannot see at a glance whether the
// AML / Thirdfort check has been sent, is in progress, or is complete.
// Visibility only — gate enforcement stays in CMRA / SoF subsystems.
const STAGES = [
  { key: "conflict",    label: "Conflict",       shortLabel: "Conflict" },
  // Label flips between the action verb ("Send OB") while pending and the
  // past-tense audit label ("OB Sent") once status === "done". The strip
  // render at line ~1181 picks `doneLabel` when status is done.
  { key: "ob_sent",     label: "Send OB",        shortLabel: "Send OB",   doneLabel: "OB Sent" },
  { key: "ob_returned", label: "OB Back",        shortLabel: "OB Back" },
  { key: "aml_check",   label: "AML / Thirdfort", shortLabel: "AML" },
  { key: "forf_draft",  label: "FORF Draft",     shortLabel: "FORF" },
  { key: "ccl_draft",   label: "CCL Draft",      shortLabel: "CCL" },
  { key: "ccl_signed",  label: "CCL Signed",     shortLabel: "Signed" },
];

const STAGE_LABELS = {
  conflict:    "Conflict Passed & SP Folders",
  ob_sent:     "OB Sent",
  ob_returned: "OB Returned",
  aml_check:   "AML / Thirdfort check in progress",
  forf_draft:  "FORF & Risk Assessment in Draft",
  ccl_draft:   "CCL Draft Ready — AI generated, needs review",
  ccl_signed:  "CCL & Welcome Pack Sent — awaiting client sign-off",
};

// 20 May 2026 (Vanessa): the `ccl_draft` entry used to point at
//   /pre-caseminder?matter={ref}&tab=ccl
// which is the intake REGISTRATION form, not the CCL editor. The page
// ignored `tab=ccl` and rendered its default "Before we begin / on
// SharePoint?" landing screen, leaving fee earners stranded with no
// visible path to the AI-polished CCL draft they expected.
//
// `ccl_draft` is intentionally OMITTED from this map now. Callers must
// resolve CCL navigation through `openMatterCcl()` (utils/openMatterCcl)
// because matter_reference → submission_id is an async lookup and the
// CCL editor lives at /submission/{id}, not at any /{ref}-keyed route.
// Click handlers below detect ccl_draft / pre_open keys and branch to
// the async helper rather than synchronously navigating to a bad URL.
// BM-H375 (7 Sep 2026): ob_sent, ob_returned and aml_check all pointed at
// /pre-caseminder?matter={ref}&tab=ob. That is the same wrong page, and the
// same inert `tab` parameter, described in the note above for ccl_draft —
// PreCaseMinder.jsx has never read `tab` at all. Only ccl_draft was fixed in
// May; these three were left. A fee earner clicking the AML stage on a live
// matter landed on a blank registration form for matters that predate
// CaseMinder, and could go no further.
//
// The destinations below are not new. BM-H282 gave the Thirdfort review panel
// a home on the matter page and the accordion's own "Open AML / Thirdfort"
// button has navigated to /matter/{ref}#thirdfort-review ever since. This map
// disagreed with that button, so where a fee earner ended up depended on
// which control they happened to click. The onboarding panel is on the same
// page. Both now agree.
// BM-H527 (15 Sep 2026): ccl_signed was left pointing at the generic
// /intake-preopen list page — the same class of bug as ccl_draft (20 May)
// and ob_sent/ob_returned/aml_check (BM-H375, 7 Sep), just missed at the
// time. A user on matter BL.HD.008.1528, sitting in ccl_signed, reported
// that "Open Full Matter" took her to a pre-open list with no matter
// context, while clicking the stage pill directly (which uses
// /matter/{ref}) correctly reached the matter detail/activity page. Routing
// ccl_signed to /matter/{ref} like ob_sent/ob_returned brings it in line
// with every other ref-specific entry in this map.
// BM-H3263 (29 Sep 2026): `conflict` is intentionally absent too. It pointed
// at /conflict-check?matter={ref}, which App.js redirects to a blank new
// conflict search, so a matter awaiting clearance could never reach the
// check already run for it, or its approval. Conflict navigation goes via
// openConflictCheck() (utils/openConflictCheck), keyed on conflict_check_id,
// which works for rows that have no matter reference yet.
const STAGE_ROUTES = {
  ob_sent:     (ref) => `/matter/${encodeURIComponent(ref)}`,
  ob_returned: (ref) => `/matter/${encodeURIComponent(ref)}`,
  aml_check:   (ref) => `/matter/${encodeURIComponent(ref)}#thirdfort-review`,
  forf_draft:  (ref) => `/forf/${encodeURIComponent(ref)}`,
  // ccl_draft: see openMatterCcl helper — intentionally absent.
  ccl_signed:  (ref) => `/matter/${encodeURIComponent(ref)}`,
};

// Stage keys whose "open" action must go via the async CCL resolver
// rather than the synchronous STAGE_ROUTES map. Kept as a Set so future
// CCL-adjacent stages (e.g. backend `pre_open`) can be added in one place.
const CCL_STAGE_KEYS = new Set(["ccl_draft", "pre_open"]);

// Map backend pipeline stages (from backend/hub/pipeline.py:PIPELINE_STAGES)
// onto the FE chip keys defined in STAGES above.
//
// Historical bug (Vanessa flagged 5+ times, May 2026): backend `onboarding_done`
// was mapped to the FE bucket `forf_draft`, even though the FE has its own
// distinct `ob_returned` bucket with its own renderer, route, downloads, and
// audit-log entry. Effect: a matter whose OB questionnaire had just been
// returned displayed under the "FORF Draft" chip even though no FORF had been
// drafted yet — confusing the team about where each matter actually was.
//
// Backend `onboarding_done` means: OB status is `complete` / `overridden` /
// `waived_by_link`, with no FORF yet. That is precisely what the FE means by
// `ob_returned` ("OB Back"). Mapping accordingly.
//
// The summary `stuck_at_ccl` aggregator below intentionally still rolls up
// `onboarding_done + forf_draft + pre_open` so the homepage card retains its
// "post-OB-back, pre-CCL-signed" semantics — only the chip placement changes.
const BACKEND_STAGE_MAP = {
  conflict_check:  "conflict",
  onboarding_sent: "ob_sent",
  onboarding_done: "ob_returned",
  // aml_check restored as a one-to-one identity map. No historical
  // bug to work around like the onboarding_done → ob_returned remap;
  // the backend stage id matches the FE chip key directly.
  aml_check:       "aml_check",
  forf_draft:      "forf_draft",
  pre_open:        "ccl_draft",
  ccl_signed:      "ccl_signed",
};

const RAG_TO_SCORE = { green: 85, amber: 55, red: 20 };

const HEALTH_COLOURS = { green: "#1aab6e", amber: "#f59e0b", red: "#ef4444", unknown: "#6b7280" };
const STUCK_COLOUR   = "#ef4444";
const DONE_COLOUR    = "#1aab6e";
const TODO_COLOUR    = "#374151";
const ACTIVE_COLOUR  = "#f59e0b";

const STAGE_DOWNLOADS = {
  conflict:    ["conflict_check"],
  ob_sent:     ["conflict_check", "ob_form", "audit_log"],
  ob_returned: ["conflict_check", "ob_form", "audit_log"],
  aml_check:   ["conflict_check", "ob_form", "audit_log"],
  forf_draft:  ["conflict_check", "ob_form", "forf", "audit_log"],
  ccl_draft:   ["conflict_check", "ob_form", "forf", "risk_assessment", "ccl", "audit_log"],
  ccl_signed:  ["conflict_check", "ob_form", "forf", "risk_assessment", "ccl", "audit_log"],
};

const DOWNLOAD_LABELS = {
  conflict_check: "Conflict Check",
  ob_form:   "OB Form",
  forf:      "FORF",
  risk_assessment: "Risk Assessment",
  // Brief 63 (23 Jun 2026): CCL download is now .docx (Calibri 11) not PDF.
  ccl:       "Letter (.docx)",
  audit_log: "Audit Log",
};

const daysColour = (days) => {
  if (days == null) return "#4b5563";
  if (days <= 3)  return "#1aab6e";
  if (days <= 7)  return "#f59e0b";
  return "#ef4444";
};

// matter_type → practice area code mapping (from PipelineMiniPanel)
function matterTypeToPracticeArea(matterType, matterRef) {
  // Try matter_type first
  if (matterType) {
    const mt = matterType.toLowerCase();
    if (mt.includes("residential") || mt.startsWith("resi")) return "001";
    if (mt.includes("corporate") || mt.includes("commercial_contract") || mt.includes("company")) return "002";
    if (mt.includes("commercial_property") || mt.includes("comm_prop") || mt.includes("comm_lease")) return "003";
    // IP before disputes: any ip_* subtype must resolve to 007, not 006.
    if (mt.includes("intellectual") || mt.startsWith("ip_") || mt === "ip") return "007";
    if (mt.includes("dispute") || mt.includes("litigation") || mt.includes("debt")) return "006";
  }
  // Fallback: infer from matter reference (e.g. BL.VEC.002.0986)
  if (matterRef) {
    const parts = matterRef.split(".");
    // Pattern: PREFIX.INITIALS.AREA.NUMBER — area is the 3-digit practice area code
    for (const p of parts) {
      if (/^0{0,2}[1-9]$/.test(p) || /^00[1-9]$/.test(p)) {
        const padded = p.padStart(3, "0");
        // 004/005 are legacy placeholders that never went live. Stage C4 of the
        // lockdown PR drops them so a stray reference does not pass the validator.
        // 007 = Intellectual Property (aligned to the SharePoint "07." folder).
        if (["001","002","003","006","007"].includes(padded)) return padded;
      }
    }
  }
  return null;
}

// ─── data adapters ────────────────────────────────────────────────────────────
export function adaptItem(raw, backendStageId) {
  const frontendStage = BACKEND_STAGE_MAP[backendStageId] || "conflict";
  const stageOrder    = STAGES.map((s) => s.key);
  const stageIdx      = stageOrder.indexOf(frontendStage);
  const completed_stages = stageOrder.slice(0, stageIdx);
  // BM-H3314 (Liezel, 30 Sep 2026, BL.RM.001.1536). Backend onboarding_done
  // means the OB is back and no FORF exists yet. It is filed in the OB Back
  // bucket (see BACKEND_STAGE_MAP) so it does not look like a FORF in draft,
  // but OB Back is DONE for such a matter, not waiting on the client, and the
  // next thing to do is the FORF. Without this the stepper read "Awaiting OB
  // return from client" and offered no way into the FORF.
  const obBack = backendStageId === "onboarding_done";
  if (obBack) completed_stages.push("ob_returned");
  const ragStr        = raw.rag || "amber";
  const health_score  = RAG_TO_SCORE[ragStr] ?? 55;
  return {
    _adapted:         true,
    backend_stage:    backendStageId,
    matter_ref:       raw.matter_reference || "",
    // BM-H3368: "Next / Waiting / Stopped / Done", worked out by the backend.
    next_step: raw.next_step || null,
    // BM-H4604 (Liezel, 22 Oct 2026, Jennie Ayling): an older matter
    // registered on the Pre-CaseMinder form. Its steps are recorded on the
    // matter page; the usual stage buttons would start them afresh.
    pre_caseminder_registered: !!raw.pre_caseminder_registered,
    pre_caseminder_submission_id: raw.pre_caseminder_submission_id || null,
    provisional_id:   raw.provisional_id || raw.provisionalId || "",
    conflict_check_id: raw.conflict_check_id || "",
    client_name:      raw.client_name || "",
    // BM-H3407: every client on a joint matter, "Smith & Jones".
    client_display_name: raw.client_display_name || raw.client_name || "",
    fee_earner_name:  raw.fee_earner || "",
    matter_type:      raw.matter_type || "",
    bottleneck_stage: frontendStage,
    completed_stages,
    is_stuck:         false,
    health_score,
    rag:              ragStr,
    days_in_stage:    raw.days_in_stage ?? null,
    created_at:       raw.created_at,
    updated_at:       raw.updated_at,
    ob_link:          raw.ob_link || null,
    ob_sent_at:       raw.ob_sent_at || null,
    ob_returned_at:   raw.ob_returned_at || null,
    ob_recipient_email: raw.ob_recipient_email || null,
    ob_email_delivery:  raw.ob_email_delivery || null,
    ob_skipped:       !!raw.ob_skipped,
    // BM-H3742: on a skipped OB, accepted / sent / not_sent.
    skip_terms_state: raw.skip_terms_state || null,
    // BM-H3314: the FORF is the next step only when AML is also satisfied, on
    // the same predicate the AML dot reads. An explicit tracker pin can place
    // a matter at onboarding_done with AML outstanding; that one waits on AML.
    ready_for_forf:   obBack && !!raw.aml_satisfied,
    linked_ob_matter_reference: raw.linked_ob_matter_reference || null,
    // BM-H278 (Vanessa, 4 Sep 2026): the backend now says outright whether
    // the OB is satisfied and why, so the stepper does not have to infer it
    // from a status vocabulary that includes a conflict_cleared stub.
    ob_status: raw.ob_status ?? null,
    // BM-H3412: the stored status. ob_status is the computed one, which reads
    // "complete" for a client who accepted, even when the accepted Terms were
    // never filed (stored "tob_save_failed"). Only the stored value says so.
    ob_status_stored: raw.ob_status_stored ?? null,
    ob_satisfied: raw.ob_satisfied ?? null,
    ob_send_withheld_reason: raw.ob_send_withheld_reason || null,
    ob_returned_offline: !!raw.ob_returned_offline,
    // BM-H270 (Vanessa, 3 Sep 2026): "we should be able to link AML like we
    // can link OB where clients have multiple folders." AML satisfaction now
    // has its own three facts, kept separate from the OB link because the two
    // halves can disagree: a matter can have OB satisfied by the link and AML
    // still genuinely outstanding, which is exactly the case that made
    // BL.VEC.002.1508 stop short of FORF.
    aml_satisfied:              !!raw.aml_satisfied,
    aml_inherited_from:         raw.aml_inherited_from || null,
    aml_original_completed_at:  raw.aml_original_completed_at || null,
    aml_inherited_stale:        !!raw.aml_inherited_stale,
    // BM-H466 (Vanessa, 9 Sep 2026), BL.VEC.006.1507 linked to
    // BL.VEC.006.0528. The backend has always worked out, in plain words, why
    // a link could not carry the AML across and why the draft file opening
    // request was then withheld. The adapter dropped both, so the row showed a
    // green "OB satisfied via linked matter" on a file that had stopped, and
    // the fee earner had nothing to read. Carry them through.
    aml_inherit_skip_reason:    raw.aml_inherit_skip_reason || null,
    aml_inherit_decided_at:     raw.aml_inherit_decided_at || null,
    // BM-H577 (Vanessa, 21 Sep 2026), BL.VEC.002.1532 linked to
    // BL.RM.001.1124. The refusal above is a snapshot taken when the link was
    // made. The backend now re-checks it against the source record and sets
    // this when the AML there has since completed, which is what offers the
    // fee earner a way to carry it across instead of a dead end.
    aml_inherit_refreshable:    !!raw.aml_inherit_refreshable,
    aml_inherit_skip_reason_recorded: raw.aml_inherit_skip_reason_recorded || null,
    forf_withheld_reason:       raw.forf_withheld_reason || null,
    // BM-H285 (Vanessa, 4 Sep 2026): "Inference is for filling gaps, not for
    // overruling a decision somebody made and the system recorded." The strip
    // used to show a stage number with no way to tell whether a person chose
    // it or the system guessed it from document state, which is how a matter
    // could be silently rolled back and still look authoritative. These two
    // carry that distinction through to the screen.
    stage_recorded:     raw.stage_recorded || null,
    stage_is_explicit:  !!raw.stage_is_explicit,
    conflict_cleared_at: raw.conflict_cleared_at || null,
    ccl_sent_at:      raw.ccl_sent_at || null,
    // BM-H3443: a CCL sent by hand outside CaseMinder, in words, or "".
    ccl_sent_manually:    raw.ccl_sent_manually || "",
    ccl_sent_manually_at: raw.ccl_sent_manually_at || null,
    // BM-H503 (Vanessa, 10 Sep 2026 19:00): "this should be marked by the
    // system, not us." The client's signature is a fact on the matter, sent by
    // the backend as ccl_signature_recorded. It is carried here so the CCL
    // Signed row can read the signature itself instead of asking the
    // separately-synced tracker stage whether the client signed - the same
    // correction BM-H284 made for AML. On BL.HD.008.1527 the client had signed
    // and the tracker had not caught up, so the row said "Awaiting CCL
    // signature" and offered Mark Signed as the only way on.
    ccl_signature_recorded: !!raw.ccl_signature_recorded,
    ccl_signed_at:    raw.ccl_signed_at || null,
    ccl_stage_sync_pending: !!raw.ccl_stage_sync_pending,
    documents:        raw.documents || [],
    // BM-H (01/09/2026): approve-path defer flag — true when the CC was
    // cleared but matter_type could not be normalised, so no BL.XX ref
    // was minted. Frontend renders an inline PA picker (see
    // AssignPracticeAreaPicker below) instead of the static
    // "Matter reference not yet allocated" placeholder.
    canonical_ref_deferred: !!raw.canonical_ref_deferred,
    canonical_ref_defer_reason: raw.canonical_ref_defer_reason || null,
    // BM-H241 (02/09/2026): Phase B mint-failure surface. Reeban Patel
    // 02/09/2026 — conflict cleared but the background SharePoint mint
    // failed silently, so canonical_ref_deferred stayed false and the FE
    // showed the static "contact admin" placeholder. We now expose the
    // failure state and render the same PA picker in that case (the
    // assign-practice-area endpoint re-runs generate_and_create_matter
    // regardless of the reason the ref is missing).
    provisioning_status: raw.provisioning_status || null,
    provisioning_error:  raw.provisioning_error  || null,
    // BM-H246 (Audit 09 F-06): backend flags rows that are stuck without
    // a canonical matter_reference. FE paints an amber ring so a
    // top-down scan of the pipeline surfaces them (otherwise days_in_stage
    // < 2 shows GREEN and the stuck row hides in plain sight).
    stuck: !!raw.stuck,
  };
}

function adaptPipelineResponse(data) {
  const matters = [];
  const stageCounts = {};
  for (const stage of (data.stages || [])) {
    stageCounts[stage.id] = stage.count || 0;
    for (const item of (stage.items || [])) {
      matters.push(adaptItem(item, stage.id));
    }
  }
  const summary = {
    total_in_flight:   data.total || 0,
    stuck_at_conflict: stageCounts.conflict_check    || 0,
    stuck_at_ob:       stageCounts.onboarding_sent   || 0,
    stuck_at_ccl:      (stageCounts.onboarding_done  || 0) + (stageCounts.aml_check || 0) + (stageCounts.forf_draft || 0) + (stageCounts.pre_open || 0),
    // BM-H3494: signed matters leave the board, so the ccl_signed column is
    // empty by design. The tile counts CCLs signed in the last 30 days.
    ready_to_open:     data.signed_last_30_days ?? (stageCounts.ccl_signed || 0),
    // ── Section 4 §3.7 — CDD renewal KPI counters ────────────────
    // Surfaced from the top-level `cdd_renewals` block on
    // /pipeline/summary. NOT a pipeline stage — parallel visibility
    // surface per MLRO-locked §3.7. Render in SummaryBar below.
    cdd_renewals_overdue:  (data.cdd_renewals && data.cdd_renewals.overdue)  || 0,
    cdd_renewals_due_soon: (data.cdd_renewals && data.cdd_renewals.due_soon) || 0,
  };
  return { matters, summary };
}

// ─── MarkSignedButton ─────────────────────────────────────────────────────────
function MarkSignedButton({ matterRef, onSuccess }) {
  const [open, setOpen]   = useState(false);
  const [notes, setNotes] = useState("");
  const [busy, setBusy]   = useState(false);
  const [err, setErr]     = useState(null);

  const handleSubmit = async (e) => {
    e.stopPropagation();
    setBusy(true);
    setErr(null);
    try {
      await axios.post(
        `${API}/ccl/manual-sign/${matterRef}`,
        { notes: notes.trim() || undefined },
        { headers: getHeaders() }
      );
      setOpen(false);
      setNotes("");
      if (onSuccess) onSuccess(matterRef);
    } catch (error) {
      setErr(_errorMessage(error, "Failed — try again"));
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        title="Mark CCL as signed (director override)"
        style={actionBtnStyle("var(--brand-dim)", "var(--brand-line)", "var(--brand-text)")}
        onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.75")}
        onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
      >
        ✓ Mark Signed
      </button>
    );
  }

  return (
    // BM-H494 (10 Sep 2026): this panel used to be drawn with
    // position: absolute; right: 0; top: 110%; minWidth: 240 inside a wrapper
    // no wider than the small "Mark Signed" button. Right-anchoring a 240px
    // panel to a ~95px trigger threw most of it to the left of the trigger,
    // where the scrolling pipeline container clipped it and, on a narrow
    // column, the viewport edge cut it. Liezel saw exactly that in UAT: the
    // heading read "CL Signed" and the notes label read only "(optional)".
    // It is now laid out in normal flow like every other confirm panel in
    // this file (SkipOBButton, MarkOBReturnedButton, SetStageOverride), so it
    // cannot be clipped and cannot leave the viewport wherever the row sits.
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        background: "var(--navy)", border: "1px solid var(--brand-line)",
        borderRadius: 8, padding: 14, marginTop: 6,
        width: "100%", maxWidth: 320, minWidth: 0, boxSizing: "border-box",
        boxShadow: "0 8px 24px rgba(0,0,0,0.5)",
      }}
    >
      <div style={{ fontSize: 12, fontWeight: 700, color: "var(--green)", marginBottom: 8 }}>
        Mark CCL Signed — {matterRef}
      </div>
      <textarea
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder="Notes (optional)"
        rows={2}
        style={textareaStyle}
      />
      {err && <div style={{ fontSize: 11, color: "var(--red)", marginBottom: 6 }}>{err}</div>}
      <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
        <button onClick={() => { setOpen(false); setNotes(""); setErr(null); }} disabled={busy} style={cancelBtnStyle}>Cancel</button>
        <button onClick={handleSubmit} disabled={busy} style={{ ...confirmBtnStyle, background: busy ? "var(--brand-hover)" : "var(--brand)", opacity: busy ? 0.7 : 1 }}>
          {busy ? "Saving…" : "Confirm"}
        </button>
      </div>
    </div>
  );
}

// ─── SendOBPanel ──────────────────────────────────────────────────────────────
function SendOBPanel({ matterRef, existingLink, sentAt, recipientEmail, onSuccess }) {
  // 20 May 2026: collapse-and-resend UX (Vanessa).
  // - Pre-fill the email input from `recipientEmail` so Resend re-opens
  //   the form with the previously-sent address, ready to confirm/edit.
  // - `sentTo` mirrors what was actually delivered so the collapsed row
  //   can surface the recipient inline ("OB sent to client@email.com").
  const [email, setEmail]   = useState(recipientEmail || "");
  const [busy, setBusy]     = useState(false);
  const [err, setErr]       = useState(null);
  const [done, setDone]     = useState(!!sentAt);
  const [sentLink, setSentLink] = useState(existingLink || null);
  const [sentTo, setSentTo]     = useState(recipientEmail || null);

  const handleSend = async (e) => {
    e.stopPropagation();
    if (!email.trim()) { setErr("Enter a client email address"); return; }
    setBusy(true);
    setErr(null);
    try {
      const res = await axios.post(
        `${API}/pipeline/send-ob`,
        { matter_ref: matterRef, client_email: email.trim() },
        { headers: getHeaders() }
      );
      setSentLink(res.data?.ob_link || null);
      setSentTo(email.trim());
      setDone(true);
      if (onSuccess) onSuccess(matterRef);
    } catch (error) {
      // 14May2026 P0 fix: route through _errorMessage so a dict-shaped
      // backend detail (e.g. ob_link_build_failed) renders as a string
      // instead of crashing React with error #31. Log the full error to
      // the console so devs can still see the raw object.
      console.error("[SendOBPanel] send-ob failed:", error);
      setErr(_errorMessage(error, "Failed to send OB link — try again"));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div style={obDoneStyle} onClick={(e) => e.stopPropagation()} data-testid="send-ob-done">
        <span style={{ fontSize: 16, marginRight: 8 }}>✅</span>
        <span style={{ color: "var(--green)", fontWeight: 600, fontSize: 13 }}>
          OB sent
          {sentTo ? (
            <>
              {" to "}
              <span style={{ color: "var(--text)", fontWeight: 500 }} data-testid="send-ob-done-recipient">
                {sentTo}
              </span>
            </>
          ) : null}
        </span>
        {sentLink && (
          <span
            onClick={() => { navigator.clipboard.writeText(sentLink); }}
            title="Copy OB link"
            style={{ marginLeft: 12, fontSize: 11, color: "var(--text-muted)", cursor: "pointer", textDecoration: "underline" }}
          >
            Copy link
          </span>
        )}
        <button
          onClick={(e) => {
            e.stopPropagation();
            // Re-open the send form pre-filled with the previously-sent
            // recipient so the user can confirm/edit before re-firing.
            // Guard with `!email` so we don't clobber a draft the user
            // already typed into the (hidden) input.
            if (sentTo && !email) setEmail(sentTo);
            setDone(false);
          }}
          style={{ marginLeft: "auto", ...ghostBtnStyle }}
          data-testid="resend-ob-btn"
        >
          Resend OB
        </button>
      </div>
    );
  }

  return (
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        marginTop: 12,
        background: "var(--navy)",
        border: "1px solid var(--brand-line)",
        borderRadius: 8,
        padding: "14px 16px",
      }}
    >
      <div style={{ fontSize: 12, fontWeight: 700, color: "var(--brand-text)", marginBottom: 10, letterSpacing: "0.04em", textTransform: "uppercase" }}>
        Send Onboarding Questionnaire
      </div>
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <div style={{ display: "flex", flex: "1 1 260px", alignItems: "center", gap: 8, background: "var(--navy)", border: `1px solid ${err ? "#ef4444" : "var(--navy-border)"}`, borderRadius: 6, padding: "7px 12px" }}>
          <span style={{ color: "var(--text-muted)", fontSize: 13 }}>✉</span>
          <input
            value={email}
            onChange={(e) => { setEmail(e.target.value); setErr(null); }}
            onKeyDown={(e) => { if (e.key === "Enter") handleSend(e); }}
            placeholder="client@email.com"
            type="email"
            autoComplete="off"
            style={{ background: "transparent", border: "none", outline: "none", color: "var(--text)", fontSize: 13, width: "100%" }}
          />
        </div>
        <button
          onClick={handleSend}
          disabled={busy}
          style={{
            background: busy ? "var(--brand-hover)" : "var(--brand)",
            border: "none", borderRadius: 6, color: "#fff",
            fontSize: 13, fontWeight: 700, padding: "8px 20px",
            cursor: busy ? "not-allowed" : "pointer", opacity: busy ? 0.7 : 1,
            whiteSpace: "nowrap", transition: "background 0.15s",
          }}
        >
          {busy ? "Sending…" : "Send OB Link →"}
        </button>
      </div>
      {err && <div style={{ fontSize: 11, color: "var(--red)", marginTop: 6 }}>{err}</div>}
      <div style={{ fontSize: 11, color: "var(--text-faint)", marginTop: 8 }}>
        Generates a unique onboarding link and emails it to the client
      </div>
    </div>
  );
}

// ─── SkipOBButton ─────────────────────────────────────────────────────────────
// 18 Jun 2026 — Vanessa hotfix. The legacy confirm path posted an
// empty body to /pipeline/matter/{ref}/skip-ob. After PR R (#533)
// the backend now hard-requires a reason for SRA audit and 400s on
// empty bodies. This component now captures the reason inline
// before POSTing — mirroring the proven flow already in
// OnboardingStatusView ("Skip OB Modal", data-testid="skip-ob-modal").
// BM-H3742 (Vanessa, 5 Oct 2026, BL.VEC.006.1555): the server's reasons for
// not sending the Terms of Business, in plain words.
function termsNotSentWords(status, error) {
  if (error === "no_client_email") {
    return "There is no client email on this matter, so the Terms of Business were not sent. Use Send Terms of Business on the OB step, or send them by post.";
  }
  if (error === "already_accepted") {
    return "The client has already accepted the Terms of Business, so they were not sent again.";
  }
  return `The Terms of Business were not sent (${error || status}).`;
}

function SkipOBButton({ matterRef, onSuccess, clientEmail }) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy]             = useState(false);
  const [err, setErr]               = useState(null);
  const [reason, setReason]         = useState("");
  // BM-H3742: the Terms of Business go to this address. Prefilled when the
  // matter already holds one; otherwise the skip used to send nothing.
  const [email, setEmail]           = useState(clientEmail || "");
  // BM-H4683 (Vanessa, 24 Oct 2026): Skip OB stands on an existing matter
  // whose completed onboarding covers this client. The server checks it.
  const [linkedRef, setLinkedRef]   = useState("");

  const handleSkip = async (e) => {
    e.stopPropagation();
    const trimmed = (reason || "").trim();
    if (!linkedRef.trim()) {
      setErr("Give the existing matter whose onboarding covers this client.");
      return;
    }
    if (!trimmed) {
      setErr("A reason is required — SRA audit trail.");
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const res = await axios.post(
        `${API}/pipeline/matter/${matterRef}/skip-ob`,
        { reason: trimmed, client_email: (email || "").trim(), linked_matter_reference: linkedRef.trim() },
        { headers: getHeaders() }
      );
      if (!res.data?.ok) {
        const msg = res.data?.detail || "Skip-OB returned unexpected response";
        setErr(msg);
        toast.error(msg);
        setBusy(false);
        return;
      }
      // Audit 05 P0-5: don't paint success unconditionally. The backend
      // returns pipeline_advanced / pipeline_advance_error / tob_email_status /
      // tob_email_error — surface any silent failure so the operator does
      // not click Skip OB again and resend the TOB email.
      const advanceFailed = res.data?.pipeline_advanced === false;
      const emailFailed = res.data?.tob_email_status && res.data.tob_email_status !== "sent" && res.data.tob_email_status !== "unknown";
      if (advanceFailed) {
        const parts = [
          `Pipeline did not advance: ${res.data?.pipeline_advance_error || "advance_pipeline reported an error — the tracker may still show OB Sent."}`,
        ];
        if (emailFailed) {
          parts.push(termsNotSentWords(res.data.tob_email_status, res.data.tob_email_error));
        }
        const warning = `OB skip recorded for ${matterRef}, but: ${parts.join(" | ")}`;
        toast.error(warning);
        setErr(warning);
        setBusy(false);
        // Do NOT close the confirm surface — operator sees the warning
        // and decides whether to escalate rather than clicking again.
        return;
      }
      if (emailFailed) {
        // BM-H3742: the skip is recorded, so this panel must not invite a
        // second Confirm Skip (1555 was skipped twice). Close it and let the
        // OB step offer Send Terms of Business instead.
        toast.error(`OB skipped for ${matterRef}. ${termsNotSentWords(res.data.tob_email_status, res.data.tob_email_error)}`, { duration: 12000 });
        setConfirming(false);
        setReason("");
        if (onSuccess) onSuccess(matterRef);
        return;
      }
      toast.success(`OB skipped for ${matterRef}. Terms of Business sent to the client.`);
      setConfirming(false);
      setReason("");
      if (onSuccess) onSuccess(matterRef);
    } catch (error) {
      const msg = _errorMessage(error, "Skip OB failed — please try again");
      setErr(msg);
      toast.error(msg);
      setBusy(false);
    }
  };

  if (!confirming) {
    return (
      <button
        onClick={(e) => { e.stopPropagation(); setConfirming(true); }}
        title="Skip OB: this client's onboarding is already complete on another matter. The client agrees the Terms of Business by email. AML is still needed."
        style={actionBtnStyle("#1a2810", "#4ade80", "#4ade80")}
        onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.75")}
        onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
      >
        ⏭ Skip OB
      </button>
    );
  }

  return (
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        display: "flex", flexDirection: "column", gap: 6,
        background: "var(--green-dim)", border: "1px solid #4ade80",
        borderRadius: 6, padding: "10px 12px", marginTop: 8, minWidth: 200,
      }}
    >
      <div style={{ fontSize: 12, fontWeight: 700, color: "var(--green)" }}>Skip OB for {matterRef}?</div>
      <div style={{ fontSize: 11, color: "var(--text-muted)" }}>Only where this client's onboarding is already complete on another matter. Marks OB as not required and emails the client the Terms of Business to agree. AML is still needed before the FORF. A reason is required for the SRA audit log.</div>
      <input
        type="text"
        value={linkedRef}
        onChange={(e) => setLinkedRef(e.target.value)}
        placeholder="Matter whose onboarding covers this client, e.g. BL.VEC.001.1500"
        disabled={busy}
        data-testid="pipeline-skip-ob-linked-ref"
        style={{
          width: "100%", boxSizing: "border-box",
          padding: "6px 8px", borderRadius: 4,
          border: "1px solid var(--navy-border)",
          background: "var(--navy-deep)", color: "#fff",
          fontSize: 12, fontFamily: "inherit",
        }}
      />
      <input
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="Client email for the Terms of Business"
        disabled={busy}
        data-testid="pipeline-skip-ob-email"
        style={{
          width: "100%", boxSizing: "border-box",
          padding: "6px 8px", borderRadius: 4,
          border: "1px solid var(--navy-border)",
          background: "var(--navy-deep)", color: "#fff",
          fontSize: 12, fontFamily: "inherit",
        }}
      />
      <textarea
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="e.g. existing company client, admin override, in-person ID verified"
        disabled={busy}
        data-testid="pipeline-skip-ob-reason"
        rows={3}
        style={{
          width: "100%", boxSizing: "border-box",
          padding: "6px 8px", borderRadius: 4,
          border: "1px solid var(--navy-border)",
          background: "var(--navy-deep)", color: "#fff",
          fontSize: 12, fontFamily: "inherit", resize: "vertical",
        }}
      />
      {err && <div style={{ fontSize: 11, color: "var(--red)" }}>{err}</div>}
      <div style={{ display: "flex", gap: 6 }}>
        <button onClick={() => { setConfirming(false); setErr(null); setReason(""); setLinkedRef(""); }} disabled={busy} style={cancelBtnStyle}>Cancel</button>
        <button
          onClick={handleSkip}
          disabled={busy || !reason.trim() || !linkedRef.trim()}
          data-testid="pipeline-confirm-skip-ob"
          style={{ ...confirmBtnStyle, background: busy ? "var(--brand-hover)" : "var(--brand)", opacity: (busy || !reason.trim() || !linkedRef.trim()) ? 0.7 : 1 }}
        >
          {busy ? "Saving…" : "Confirm Skip"}
        </button>
      </div>
    </div>
  );
}

// ─── SendSkipTermsButton ─────────────────────────────────────────────────────
// BM-H3742 (Vanessa, 5 Oct 2026, BL.VEC.006.1555): "i want to skip OB but
// can't see how to send terms". A skip with no client email on file recorded
// the skip and sent nothing, and the page had no way to send the terms after.
// Shown on a skipped OB whose terms have not gone; asks for the email if the
// matter has none.
function SendSkipTermsButton({ matterRef, clientEmail, onSuccess }) {
  const [open, setOpen]   = useState(false);
  const [busy, setBusy]   = useState(false);
  const [err, setErr]     = useState(null);
  const [email, setEmail] = useState(clientEmail || "");

  const send = async (e) => {
    e.stopPropagation();
    setBusy(true);
    setErr(null);
    try {
      const res = await axios.post(
        `${API}/pipeline/matter/${encodeURIComponent(matterRef)}/skip-ob/send-terms`,
        { client_email: (email || "").trim() },
        { headers: getHeaders() }
      );
      toast.success(`Terms of Business sent to ${res.data?.sent_to || "the client"}.`);
      setOpen(false);
      if (onSuccess) onSuccess(matterRef);
    } catch (error) {
      const msg = _errorMessage(error, "Could not send the Terms of Business");
      setErr(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        data-testid="send-skip-terms"
        title="Email the client the Terms of Business to agree"
        style={actionBtnStyle("#1a2810", "#4ade80", "#4ade80")}
      >
        ✉ Send Terms of Business
      </button>
    );
  }
  return (
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        display: "flex", flexDirection: "column", gap: 6,
        background: "var(--green-dim)", border: "1px solid #4ade80",
        borderRadius: 6, padding: "10px 12px", marginTop: 8, minWidth: 240,
      }}
    >
      <div style={{ fontSize: 11, color: "var(--text-muted)" }}>
        The client gets a link to read and agree the Terms of Business.
      </div>
      <input
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="Client email"
        disabled={busy || !!clientEmail}
        data-testid="send-skip-terms-email"
        style={{
          width: "100%", boxSizing: "border-box",
          padding: "6px 8px", borderRadius: 4,
          border: "1px solid var(--navy-border)",
          background: "var(--navy-deep)", color: "#fff",
          fontSize: 12, fontFamily: "inherit",
        }}
      />
      {err && <div style={{ fontSize: 11, color: "var(--red)" }}>{err}</div>}
      <div style={{ display: "flex", gap: 6 }}>
        <button onClick={() => { setOpen(false); setErr(null); }} disabled={busy} style={cancelBtnStyle}>Cancel</button>
        <button
          onClick={send}
          disabled={busy || !(email || "").trim()}
          data-testid="send-skip-terms-confirm"
          style={{ ...confirmBtnStyle, opacity: (busy || !(email || "").trim()) ? 0.7 : 1 }}
        >
          {busy ? "Sending…" : "Send"}
        </button>
      </div>
    </div>
  );
}

// BM-H3742: where a skipped OB's terms stand, with the send button when they
// have not gone.
function SkipTermsStatus({ matter, matterRef, onSuccess }) {
  if (!matter.ob_skipped || !matter.skip_terms_state) return null;
  if (matter.skip_terms_state === "accepted") {
    return <span style={{ fontSize: 10, color: "var(--text-faint)" }} data-testid="skip-terms-state">· Terms accepted</span>;
  }
  if (matter.skip_terms_state === "sent") {
    return <span style={{ fontSize: 10, color: "var(--text-faint)" }} data-testid="skip-terms-state">· Terms sent, awaiting client</span>;
  }
  return (
    <>
      <span style={{ fontSize: 10, color: "var(--amber)", fontWeight: 600 }} data-testid="skip-terms-state">
        Terms of Business not sent
      </span>
      <SendSkipTermsButton
        matterRef={matterRef}
        clientEmail={matter.ob_recipient_email}
        onSuccess={onSuccess}
      />
    </>
  );
}

// ─── MarkOBReturnedButton ────────────────────────────────────────────────────
// 2026-09-01 (Sharon / Liezel workflow): the client did complete OB but not
// via the form (email reply, signed PDF, in-person). The pipeline sits
// stuck at "Send OB" because the auto-advance never fires without a form
// submission. This button captures the offline completion, records a proof
// reference for the SRA audit trail, sets ob_returned_at server-side, and
// re-runs advance_pipeline() so the stage moves on. NOT the same as skip-ob
// (which is for cases where OB never happened and TOB-for-agreement is sent).
// BM-H3412 (2 Oct 2026): the client accepted the Terms but filing the PDF to
// SharePoint failed. Files the snapshot the client accepted. Looks first (dry
// run), says what it will do, and files only on a yes.
function RetryTobFilingButton({ matterRef, onSuccess }) {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const url = `${API}/onboarding/retry-tob-filing/${encodeURIComponent(matterRef)}`;
      const look = (await axios.post(url, { dry_run: true })).data || {};
      if (!look.matter_folder_found) {
        toast.error(`The matter folder for ${matterRef} cannot be found on SharePoint${look.matter_folder_error ? ` (${look.matter_folder_error})` : ""}. Nothing was filed.`);
        return;
      }
      const ok = window.confirm(
        `File the Terms of Business the client accepted to ${look.destination}, as ${look.file_name}, and move this onboarding to "${look.would_set_status}"?`
      );
      if (!ok) return;
      const res = await axios.post(url, { dry_run: false, reason: "Retried from the matter page" });
      toast.success(`Accepted Terms filed for ${matterRef}; onboarding now ${res.data?.status}`);
      onSuccess && onSuccess();
    } catch (e) {
      toast.error(errorMessage(e, "Filing failed again; the reason is on the record"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      type="button"
      onClick={run}
      disabled={busy}
      data-testid={`retry-tob-filing-${matterRef}`}
      style={{ fontSize: 11, padding: "3px 8px", borderRadius: 4, border: "1px solid #f59e0b", color: "#f59e0b", background: "transparent", cursor: busy ? "wait" : "pointer" }}
    >
      {busy ? "Filing..." : "Retry filing"}
    </button>
  );
}

// BM-H3509: add a joint client after onboarding has gone out. The only
// button for this lived in OnboardingStatusView, which the dashboard shows
// to Accounts alone, so fee earners and admin had no way to do it. Same
// endpoint; the server sends the link now if the main client has signed,
// or when they sign.
function AddJointClientButton({ matterRef, onSuccess }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", mobile: "" });
  const submit = async () => {
    setBusy(true);
    try {
      const res = await axios.post(
        `${API}/onboarding/add-joint-client/${encodeURIComponent(matterRef)}`, form,
      );
      const d = res.data || {};
      if (d.invitation_sent) toast.success(`${form.name} added and sent their onboarding link`);
      else if (d.invitation_deferred) toast.success(`${form.name} added. Their link goes out when the main client signs.`);
      else toast.error(`${form.name} added, but the email did not send: ${d.invitation_error || "unknown error"}. Use Resend OB Invite to try again.`);
      setOpen(false);
      setForm({ name: "", email: "", mobile: "" });
      onSuccess && onSuccess();
    } catch (e) {
      toast.error(errorMessage(e, "Could not add the joint client"));
    } finally {
      setBusy(false);
    }
  };
  const btn = { fontSize: 11, padding: "3px 8px", borderRadius: 4, border: "1px solid var(--brand)", color: "var(--brand)", background: "transparent", cursor: busy ? "wait" : "pointer" };
  const box = { fontSize: 11, padding: "3px 6px", borderRadius: 4, border: "1px solid var(--border, #334155)", background: "transparent", color: "inherit" };
  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)} style={btn} data-testid={`pipeline-add-joint-${matterRef}`}>
        Add joint client
      </button>
    );
  }
  return (
    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }} data-testid={`pipeline-add-joint-form-${matterRef}`}>
      <input style={box} placeholder="Full name" value={form.name} autoFocus
        onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="pipeline-add-joint-name" />
      <input style={box} placeholder="Email" type="email" value={form.email}
        onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} data-testid="pipeline-add-joint-email" />
      <input style={box} placeholder="Mobile (for Thirdfort)" value={form.mobile}
        onChange={(e) => setForm((f) => ({ ...f, mobile: e.target.value }))} data-testid="pipeline-add-joint-mobile" />
      <button type="button" onClick={submit} style={btn}
        disabled={busy || !form.name.trim() || !form.email.trim()} data-testid="pipeline-add-joint-submit">
        {busy ? "Adding..." : "Add and send link"}
      </button>
      <button type="button" onClick={() => setOpen(false)} style={{ ...btn, border: "none", color: "var(--text-faint)" }}>Cancel</button>
    </div>
  );
}

function MarkOBReturnedButton({ matterRef, onSuccess }) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy]             = useState(false);
  const [err, setErr]               = useState(null);
  const [proof, setProof]           = useState("");

  const handleMark = async (e) => {
    e.stopPropagation();
    const trimmed = (proof || "").trim();
    if (!trimmed) {
      setErr("A proof reference is required — SRA audit trail.");
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const res = await axios.post(
        `${API}/pipeline/matter/${matterRef}/mark-ob-returned`,
        { proof: trimmed },
        { headers: getHeaders() }
      );
      if (!res.data?.ok) {
        const msg = res.data?.detail || "Mark-OB-returned returned unexpected response";
        setErr(msg);
        toast.error(msg);
        setBusy(false);
        return;
      }
      if (res.data?.idempotent_replay) {
        toast.success(`OB already recorded as returned for ${matterRef}`);
      } else {
        toast.success(`OB marked returned offline for ${matterRef} — advancing`);
      }
      setConfirming(false);
      setProof("");
      if (onSuccess) onSuccess(matterRef);
    } catch (error) {
      const msg = _errorMessage(error, "Mark OB returned failed — please try again");
      setErr(msg);
      toast.error(msg);
      setBusy(false);
    }
  };

  if (!confirming) {
    return (
      <button
        onClick={(e) => { e.stopPropagation(); setConfirming(true); }}
        data-testid="pipeline-mark-ob-returned"
        title="Client already returned OB offline (email reply, signed PDF, in person) — record proof and advance"
        style={actionBtnStyle("#10202a", "#38bdf8", "#38bdf8")}
        onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.75")}
        onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
      >
        ✅ OB Returned Offline
      </button>
    );
  }

  return (
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        display: "flex", flexDirection: "column", gap: 6,
        background: "#0b1e2a", border: "1px solid #38bdf8",
        borderRadius: 6, padding: "10px 12px", marginTop: 8, minWidth: 260,
      }}
    >
      <div style={{ fontSize: 12, fontWeight: 700, color: "#7dd3fc" }}>Mark OB returned for {matterRef}?</div>
      <div style={{ fontSize: 11, color: "var(--text-muted)" }}>Records that OB was completed outside the app and advances the pipeline. A proof reference is required for the SRA audit log.</div>
      <textarea
        value={proof}
        onChange={(e) => setProof(e.target.value)}
        placeholder="e.g. client replied to OB email 28 Aug, signed PDF in /Correspondence, in-person ID verified 29 Aug by RM"
        disabled={busy}
        data-testid="pipeline-mark-ob-returned-proof"
        rows={3}
        style={{
          width: "100%", boxSizing: "border-box",
          padding: "6px 8px", borderRadius: 4,
          border: "1px solid var(--navy-border)",
          background: "var(--navy-deep)", color: "#fff",
          fontSize: 12, fontFamily: "inherit", resize: "vertical",
        }}
      />
      {err && <div style={{ fontSize: 11, color: "var(--red)" }}>{err}</div>}
      <div style={{ display: "flex", gap: 6 }}>
        <button onClick={() => { setConfirming(false); setErr(null); setProof(""); }} disabled={busy} style={cancelBtnStyle}>Cancel</button>
        <button
          onClick={handleMark}
          disabled={busy || !proof.trim()}
          data-testid="pipeline-confirm-mark-ob-returned"
          style={{ ...confirmBtnStyle, background: busy ? "#082032" : "#0284c7", opacity: (busy || !proof.trim()) ? 0.7 : 1 }}
        >
          {busy ? "Saving…" : "Confirm Returned"}
        </button>
      </div>
    </div>
  );
}

// ─── SetStageOverride ─────────────────────────────────────────────────────────
const OVERRIDE_STAGES = [
  { id: "conflict_check",  label: "Conflict Check" },
  { id: "onboarding_sent", label: "OB Sent" },
  { id: "onboarding_done", label: "OB Complete" },
  { id: "forf_draft",      label: "FORF Draft" },
  { id: "pre_open",        label: "CCL Draft" },
  { id: "ccl_signed",      label: "CCL Signed" },
];

// BM-H (01/09/2026): AssignPracticeAreaPicker
//
// Self-service unblock for pipeline rows in canonical_ref_deferred=True
// state (approve honoured, matter_type unresolved, no BL.XX ref minted).
// Reeken Patel 01/09/2026: conflict cleared but Send OB gate had no exit
// path — fee earner had to escalate to admin. This component POSTs to
// /api/pipeline/matter/{cc_id}/assign-practice-area which validates the
// PA code and re-runs the mint.
//
// The picker uses hard-coded active PA codes matching the backend's
// ACTIVE_PRACTICE_AREA_CODES (hub/matter_type_taxonomy.py). Kept in sync
// with the backend list — if a new PA is activated there, add it here.
const _ASSIGN_PA_OPTIONS = [
  { code: "01 RESI",                label: "01 RESI — Residential Property" },
  { code: "02 COCO",                label: "02 COCO — Company & Commercial" },
  { code: "03 Commercial Property", label: "03 Commercial Property" },
  { code: "06 COCO Disputes",       label: "06 COCO Disputes" },
  { code: "07 IP",                  label: "07 IP — Intellectual Property" },
  { code: "08 Private Client",      label: "08 Private Client (Wills & LPA)" },
];

function AssignPracticeAreaPicker({ conflictCheckId, deferReason, onSuccess }) {
  const [selected, setSelected] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const handleAssign = async () => {
    if (!selected || !conflictCheckId) return;
    setBusy(true); setErr("");
    try {
      const res = await axios.post(
        `${API}/pipeline/matter/${encodeURIComponent(conflictCheckId)}/assign-practice-area`,
        { practice_area: selected },
        { headers: getHeaders() },
      );
      const ref = res.data && res.data.matter_reference;
      toast.success(
        ref
          ? `Matter reference minted: ${ref}`
          : "Practice area assigned"
      );
      // Pass the new matter_reference so the pipeline auto-expands the
      // now-mint-ed row (matches SendOBPanel etc.) on refetch.
      if (onSuccess) onSuccess(ref);
    } catch (error) {
      setErr(errorMessage(error, "Failed to assign practice area"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ fontSize: 11, color: "var(--text-muted)", lineHeight: 1.5 }}>
      <div style={{ fontWeight: 600, color: "var(--amber)", marginBottom: 2 }}>
        Matter reference not yet allocated
      </div>
      <div style={{ marginBottom: 6 }}>
        Practice area could not be resolved when the conflict check was
        approved. Pick one below to mint the matter reference and unlock
        Send OB.
      </div>
      {deferReason && (
        <div style={{ fontSize: 10, color: "var(--text-faint)", marginBottom: 6 }}>
          Reason: {deferReason}
        </div>
      )}
      <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
        <select
          value={selected}
          onChange={(e) => setSelected(e.target.value)}
          disabled={busy}
          style={{
            fontSize: 11,
            padding: "3px 6px",
            background: "var(--bg-card)",
            color: "var(--text)",
            border: "1px solid var(--border)",
            borderRadius: 4,
            maxWidth: 220,
          }}
        >
          <option value="">Choose practice area…</option>
          {_ASSIGN_PA_OPTIONS.map((pa) => (
            <option key={pa.code} value={pa.code}>{pa.label}</option>
          ))}
        </select>
        <button
          onClick={handleAssign}
          disabled={busy || !selected || !conflictCheckId}
          style={{
            fontSize: 11,
            padding: "4px 10px",
            background: (busy || !selected) ? "#374151" : "var(--accent)",
            color: "#fff",
            border: 0,
            borderRadius: 4,
            cursor: (busy || !selected) ? "not-allowed" : "pointer",
          }}
        >
          {busy ? "Minting…" : "Assign & mint"}
        </button>
      </div>
      {!conflictCheckId && (
        <div style={{ fontSize: 10, color: "var(--text-faint)", marginTop: 4 }}>
          No conflict check ID on this row — contact your admin.
        </div>
      )}
      {err && (
        <div style={{ fontSize: 11, color: "var(--rag-red)", marginTop: 4 }}>
          {err}
        </div>
      )}
    </div>
  );
}

// BM-H3864: exported so the override can be driven in a test.
export function SetStageOverride({ matterRef, currentStage, onSuccess }) {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState("");
  const [notes, setNotes] = useState("");
  const [effectiveDate, setEffectiveDate] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const handleSet = async () => {
    if (!selected) return;
    setBusy(true); setErr("");
    try {
      const payload = { stage: selected, notes: notes || "Manual override" };
      if (effectiveDate) payload.effective_at = new Date(effectiveDate + "T12:00:00Z").toISOString();
      // BM-H3864: this called api.post, and no `api` exists in this module,
      // so every override failed before it was sent.
      await axios.post(`${API}/pipeline/matter/${encodeURIComponent(matterRef)}/set-stage`, payload);
      setOpen(false); setSelected(""); setNotes(""); setEffectiveDate("");
      if (onSuccess) onSuccess(matterRef);
    } catch (error) {
      setErr(_errorMessage(error, "Failed to set stage"));
    } finally { setBusy(false); }
  };

  if (!open) return (
    <button onClick={() => setOpen(true)} style={{ ...ghostBtnStyle, fontSize: 11, color: "var(--amber)" }} title="Manually override pipeline stage">
      ⚙ Set Stage
    </button>
  );

  return (
    <div style={{ background: "var(--navy-card)", border: "1px solid var(--amber)", borderRadius: 6, padding: 10, marginTop: 6, minWidth: 260 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: "var(--amber)", marginBottom: 8 }}>Override stage for {matterRef}</div>
      <select
        value={selected}
        onChange={e => setSelected(e.target.value)}
        style={{ width: "100%", padding: "6px 8px", borderRadius: 4, border: "1px solid var(--navy-border)", background: "var(--navy-deep)", color: "#fff", fontSize: 12, marginBottom: 6 }}
      >
        <option value="">Select stage…</option>
        {OVERRIDE_STAGES.map(s => (
          <option key={s.id} value={s.id} disabled={s.id === currentStage}>
            {s.label}{s.id === currentStage ? " (current)" : ""}
          </option>
        ))}
      </select>
      <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 4 }}>Date stage actually happened (leave blank for today)</div>
      <input
        type="date"
        value={effectiveDate}
        onChange={e => setEffectiveDate(e.target.value)}
        max={new Date().toISOString().slice(0, 10)}
        style={{ width: "100%", padding: "6px 8px", borderRadius: 4, border: "1px solid var(--navy-border)", background: "var(--navy-deep)", color: "#fff", fontSize: 12, marginBottom: 6, boxSizing: "border-box" }}
      />
      <input
        type="text" placeholder="Notes (e.g. completed manually)"
        value={notes} onChange={e => setNotes(e.target.value)}
        style={{ width: "100%", padding: "6px 8px", borderRadius: 4, border: "1px solid var(--navy-border)", background: "var(--navy-deep)", color: "#fff", fontSize: 12, marginBottom: 8, boxSizing: "border-box" }}
      />
      {err && <div style={{ color: "var(--red)", fontSize: 11, marginBottom: 6 }}>{err}</div>}
      <div style={{ display: "flex", gap: 8 }}>
        <button onClick={handleSet} disabled={!selected || busy} style={{ ...confirmBtnStyle, background: busy ? "#333" : "var(--amber)", color: "#000", opacity: (!selected || busy) ? 0.5 : 1, fontSize: 11, padding: "4px 12px" }}>
          {busy ? "Setting…" : "Confirm"}
        </button>
        <button onClick={() => { setOpen(false); setErr(""); }} style={{ ...ghostBtnStyle, fontSize: 11 }}>Cancel</button>
      </div>
    </div>
  );
}

// ─── DeleteMatterButton ───────────────────────────────────────────────────────
// Trust-context: the button accepts EITHER a matter_ref/provisional_id OR a
// conflict_check_id, and routes to the matching endpoint:
//
//   * matterRef present     → DELETE /api/pipeline/matter/{ref}
//   * conflictCheckId only  → DELETE /api/pipeline/conflict-check/{cc_id}
//     (for pre-CaseMinder rows whose matter_reference + provisional_id are
//     both empty — see Liezel diagnosis 13 May 2026, PR #428 didn't cover
//     this shape because both fallbacks evaluated falsy)
//
// When BOTH are blank, the URL would collapse to a no-segment path and
// FastAPI would return its stock `{"detail":"Not Found"}` — historically
// rendered as a useless "Not Found" inside the delete popover (see
// Liezel-delete-still-broken incident, 11 May 2026). Guard both at the call
// site (don't render at all when there's nothing to send) AND inside the
// component (refuse to fire the axios call; surface a real error verbatim).
//
// Error rendering goes through `window.errorMessage(err, fallback)` so the
// user sees the real backend `detail` rather than a generic "Delete failed".
// NO silent fallbacks, NO swallowed exceptions, NO false-success toasts —
// Vanessa is uncompromising on this.
//
// 14May2026 hardening (P0 fallout): backend HTTPException.detail is
// sometimes a dict (e.g. pipeline.py:774 ob_link_build_failed returns
// {error, message, ...}; pipeline.py:281 ccl_template_missing_for_matter_type
// same shape). The previous `err.response.data.detail || ...` pattern
// short-circuited to the object, which then got rendered as a React child
// at lines 265/370/437/503/627 and crashed the whole PipelineTracker with
// React error #31 ("Objects are not valid as a React child"). This helper
// now coerces dict-shaped details to a readable string.
function _errorMessage(err, fallback) {
  if (typeof err === "string") return err || fallback;
  return errorMessage(err, fallback);
}

// Back-compat alias — keeps DeleteMatterButton call sites unchanged while
// other panels in this file migrate to the renamed helper.
const _deleteErrorMessage = _errorMessage;

function DeleteMatterButton({ matterRef, provisionalId, conflictCheckId, onDeleted }) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy]             = useState(false);
  const [err, setErr]               = useState(null);

  const trimmedRef  = typeof matterRef === "string" ? matterRef.trim() : "";
  const trimmedProv = typeof provisionalId === "string" ? provisionalId.trim() : "";
  const trimmedCc   = typeof conflictCheckId === "string" ? conflictCheckId.trim() : "";
  const hasRef      = trimmedRef.length > 0;
  const hasProv     = trimmedProv.length > 0;
  const hasCc       = trimmedCc.length > 0;
  const canDelete   = hasRef || hasProv || hasCc;
  // Endpoint preference: matter_ref → provisional_id → conflict_check_id.
  // matter_ref and provisional_id both resolve via /pipeline/matter/{ref}
  // (its cascade covers tracker + OB rows). The conflict-check route is
  // only for the pre-CaseMinder shape (bare CC, no matter_ref, no OB).
  let url = null;
  let label = "";
  if (hasRef) {
    url = `${API}/pipeline/matter/${trimmedRef}`;
    label = trimmedRef;
  } else if (hasProv) {
    url = `${API}/pipeline/matter/${trimmedProv}`;
    label = trimmedProv;
  } else if (hasCc) {
    url = `${API}/pipeline/conflict-check/${trimmedCc}`;
    label = trimmedCc;
  }

  const handleDelete = async (e) => {
    e.stopPropagation();
    if (!canDelete) {
      // Should never happen — every pipeline row has at least conflict_check_id.
      // Surface explicitly via window.errorMessage; do NOT silently no-op.
      setErr(_deleteErrorMessage(new Error("row has no identifier"), "Cannot delete: row has no identifier"));
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await axios.delete(url, { headers: getHeaders() });
      if (onDeleted) onDeleted();
    } catch (error) {
      setErr(_deleteErrorMessage(error, "Delete failed"));
      setBusy(false);
      setConfirming(false);
    }
  };

  if (!confirming) {
    return (
      <button
        onClick={(e) => { e.stopPropagation(); setConfirming(true); }}
        title={canDelete ? "Delete this matter from pipeline" : "This matter has no reference yet"}
        style={actionBtnStyle("#2d0f0f", "#ef4444", "#ef4444")}
        onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.75")}
        onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
      >
        🗑 Delete
      </button>
    );
  }

  return (
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        display: "flex", flexDirection: "column", gap: 6,
        background: "#1a0606", border: "1px solid #ef4444",
        borderRadius: 6, padding: "10px 12px", minWidth: 200,
      }}
    >
      <div style={{ fontSize: 12, fontWeight: 700, color: "var(--red)" }}>Delete {label || "(unknown)"}?</div>
      <div style={{ fontSize: 11, color: "var(--text-muted)" }}>This cannot be undone.</div>
      {!canDelete && (
        <div style={{ fontSize: 11, color: "var(--red)" }}>
          This matter has no reference yet — refresh the page; if it persists, ping the dev.
        </div>
      )}
      {err && <div style={{ fontSize: 11, color: "var(--red)" }}>{err}</div>}
      <div style={{ display: "flex", gap: 6 }}>
        <button onClick={() => setConfirming(false)} disabled={busy} style={cancelBtnStyle}>Cancel</button>
        <button
          onClick={handleDelete}
          disabled={busy || !canDelete}
          style={{ ...confirmBtnStyle, background: (busy || !canDelete) ? "#2d0f0f" : "#ef4444", opacity: (busy || !canDelete) ? 0.5 : 1 }}
        >
          {busy ? "Deleting…" : "Yes, delete"}
        </button>
      </div>
    </div>
  );
}

// ─── DownloadBar ──────────────────────────────────────────────────────────────
function DownloadBar({ matterRef, bottleneckStage }) {
  const [busy, setBusy] = useState({});
  const [availableDocs, setAvailableDocs] = useState(null);

  // Fetch actually-available documents from the backend so every historical
  // record is downloadable regardless of the current pipeline stage.
  useEffect(() => {
    if (!matterRef) return;
    let cancelled = false;
    axios
      .get(`${API}/pipeline/matter/${matterRef}/documents`, { headers: getHeaders() })
      .then((res) => {
        if (!cancelled && res.data?.documents) {
          setAvailableDocs(res.data.documents.map((d) => d.type));
        }
      })
      .catch(() => {
        // Fall back to stage-gated list on error
        if (!cancelled) setAvailableDocs(null);
      });
    return () => { cancelled = true; };
  }, [matterRef]);

  // Use fetched docs if available, otherwise fall back to stage-gated list
  const docTypes = availableDocs && availableDocs.length > 0
    ? availableDocs
    : STAGE_DOWNLOADS[bottleneckStage] || ["audit_log"];

  const handleClick = (e, docType) => {
    e.stopPropagation();
    downloadPipelineDoc(matterRef, docType, (b) =>
      setBusy((prev) => ({ ...prev, [docType]: b }))
    );
  };

  return (
    <div style={{
      display: "flex", flexWrap: "wrap", gap: 8,
      borderTop: "1px solid var(--navy-border)", paddingTop: 14, marginTop: 4,
    }}>
      {docTypes.map((dt) => (
        <button
          key={dt}
          onClick={(e) => handleClick(e, dt)}
          disabled={!!busy[dt]}
          title={`Download ${DOWNLOAD_LABELS[dt]}`}
          style={{
            display: "inline-flex", alignItems: "center", gap: 5,
            background: "transparent",
            border: "1px solid #2a3a4a",
            borderRadius: 5,
            color: busy[dt] ? "#4b5563" : "#9ca3af",
            fontSize: 11, fontWeight: 600,
            padding: "5px 12px",
            cursor: busy[dt] ? "not-allowed" : "pointer",
            transition: "color 0.15s, border-color 0.15s",
            opacity: busy[dt] ? 0.6 : 1,
          }}
          onMouseEnter={(e) => { if (!busy[dt]) { e.currentTarget.style.color = "#e5e7eb"; e.currentTarget.style.borderColor = "#4b5563"; } }}
          onMouseLeave={(e) => { e.currentTarget.style.color = busy[dt] ? "#4b5563" : "#9ca3af"; e.currentTarget.style.borderColor = "#2a3a4a"; }}
        >
          <span style={{ fontSize: 13 }}>{busy[dt] ? "⏳" : "↓"}</span>
          {busy[dt] ? "Downloading…" : (DOWNLOAD_LABELS[dt] || dt.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()))}
        </button>
      ))}
    </div>
  );
}

// ─── TrackingTimeline ─────────────────────────────────────────────────────────
function TrackingTimeline({ matter }) {
  const events = [
    matter.created_at          && { label: "Matter Created",   date: matter.created_at,          icon: "🌱" },
    matter.conflict_cleared_at && { label: "Conflict Cleared", date: matter.conflict_cleared_at, icon: "✅" },
    matter.ob_sent_at          && { label: "OB Sent",          date: matter.ob_sent_at,           icon: "✉" },
    matter.ob_returned_at      && { label: "OB Returned",      date: matter.ob_returned_at,       icon: "📥" },
    matter.ccl_sent_at         && { label: "CCL Sent",         date: matter.ccl_sent_at,          icon: "📋" },
    matter.ccl_sent_manually_at && { label: "CCL Sent (outside CaseMinder)", date: matter.ccl_sent_manually_at, icon: "📋" },
  ].filter(Boolean);

  if (events.length === 0) {
    return <div style={{ fontSize: 12, color: "var(--text-faint)", fontStyle: "italic" }}>No timeline events yet</div>;
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {events.map((ev, i) => (
        <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
          <div style={{ fontSize: 16, lineHeight: 1.2 }}>{ev.icon}</div>
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text)" }}>{ev.label}</div>
            <div style={{ fontSize: 11, color: "var(--text-muted)" }}>
              {new Date(ev.date).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── openStageScreen ─────────────────────────────────────────────────────────
// BM-H3775: what a stage segment on the progress track opens. This was the
// click handler of StagePill, the seven-pill strip the track replaced.
function preCmStepsPath(matter) {
  if (matter.matter_ref) return `/matter/${encodeURIComponent(matter.matter_ref)}#pre-cm-history`;
  if (matter.pre_caseminder_submission_id) return `/submission/${matter.pre_caseminder_submission_id}`;
  return null;
}

function openStageScreen(stageKey, { matterRef, conflictCheckId, navigate, preCm }) {
  // BM-H4604: a registered older matter's stages open its steps panel. The
  // conflict stage would otherwise run a new conflict check, which opens a
  // new matter number and folder (BL.HD.002.1569).
  if (preCm) {
    const path = preCmStepsPath(preCm);
    if (path) navigate(path);
    return;
  }
  if (CCL_STAGE_KEYS.has(stageKey)) {
    // Async matter_reference → submission_id lookup. The helper
    // navigates on success and surfaces a console + alert on failure;
    // we deliberately don't fall back to STAGE_ROUTES because there
    // is no working synchronous CCL URL.
    openMatterCcl({ matterRef, axios, apiBase: API, navigate });
    return;
  }
  if (stageKey === "conflict") {
    openConflictCheck({ conflictCheckId, matterRef, axios, apiBase: API, navigate });
    return;
  }
  const route = STAGE_ROUTES[stageKey];
  if (route && matterRef) navigate(route(matterRef));
}

// ─── StepperDot ──────────────────────────────────────────────────────────────
function StepperDot({ status }) {
  const size = 14;
  const colours = {
    done:   { bg: DONE_COLOUR,   border: DONE_COLOUR },
    active: { bg: ACTIVE_COLOUR, border: ACTIVE_COLOUR },
    stuck:  { bg: STUCK_COLOUR,  border: STUCK_COLOUR },
    todo:   { bg: "transparent", border: "#374151" },
  };
  const c = colours[status] || colours.todo;
  return (
    <div style={{
      width: size, height: size, borderRadius: "50%",
      background: c.bg, border: `2px solid ${c.border}`,
      display: "flex", alignItems: "center", justifyContent: "center",
      flexShrink: 0, position: "relative", zIndex: 2,
    }}>
      {status === "done" && (
        <span style={{ fontSize: 9, color: "#fff", fontWeight: 700, lineHeight: 1 }}>✓</span>
      )}
    </div>
  );
}

// ─── PreCmStageContent ───────────────────────────────────────────────────────
// BM-H4604 / BM-H4878: a stage of a registered older matter. It says whether
// the step is on record and whether CaseMinder found it in the SharePoint
// folder. A step it could not find links to the compliance step itself to
// put it right (never to a new conflict check, which mints a new number),
// and to the steps panel, where the reasons are recorded.
function PreCmStageContent({ stageKey, matter, navigate }) {
  const path = preCmStepsPath(matter);
  const step = PRE_CM_STEP_FOR_STAGE[stageKey];
  const info = usePreCmFolderState(matter.matter_ref);
  const onRecord = !!(info?.history?.steps || []).find((s) => s.key === step && s.on_record);
  const inFolder = onRecord ? "on_record" : folderState(info?.found, step);
  const link = inFolder === "missing" ? remediationLink(step, matter.matter_ref) : null;

  const message = {
    on_record: "On record",
    found: "Found in SharePoint: confirm it on the matter page",
    missing: "Not found in SharePoint: put it right, then record the reasons",
    unknown: "Done before CaseMinder: record it on the matter page",
  }[inFolder];

  return (
    <div data-testid={`pre-cm-stage-${stageKey}`} data-folder-state={inFolder}
         style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
      <span style={{ fontSize: 11, color: inFolder === "missing" ? "#f59e0b" : "var(--text-muted)" }}>
        {message}
      </span>
      {link && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            if (link.ccl) openMatterCcl({ matterRef: matter.matter_ref, axios, apiBase: API, navigate });
            else navigate(link.path);
          }}
          style={actionBtnStyle("#451a03", "#fcd34d", "#f59e0b")}
          data-testid={`pre-cm-remediate-${stageKey}`}
        >
          {link.label} →
        </button>
      )}
      {inFolder === "missing" && step === "conflict" && (
        <span style={{ fontSize: 11, color: "#fcd34d" }} data-testid="pre-cm-conflict-note">
          {PRE_CM_CONFLICT_NOTE}
        </span>
      )}
      {path && inFolder !== "on_record" && (
        <button
          onClick={(e) => { e.stopPropagation(); navigate(path); }}
          style={actionBtnStyle("#1e1b4b", "#a5b4fc", "#6366f1")}
        >
          {inFolder === "missing" ? "Record the reasons →" : "Record steps done before CaseMinder →"}
        </button>
      )}
    </div>
  );
}

// ─── StepperStageContent ─────────────────────────────────────────────────────
// BM-H266: exported so the OB stage cell can be unit tested directly
// without mounting the whole tracker. PipelineRow below still renders it
// exactly as before; nothing about the runtime behaviour changes.
export function StepperStageContent({ stageKey, status, matter, navigate, onSuccess, isDirector }) {
  const matterRef = matter.matter_ref;
  const [dlBusy, setDlBusy] = useState({});

  if (matter.pre_caseminder_registered) {
    return <PreCmStageContent stageKey={stageKey} matter={matter} navigate={navigate} />;
  }

  const dl = (e, docType) => {
    e.stopPropagation();
    downloadPipelineDoc(matterRef, docType, (b) =>
      setDlBusy((prev) => ({ ...prev, [docType]: b }))
    );
  };

  const dlBtn = (docType) => (
    <button
      key={docType}
      onClick={(e) => dl(e, docType)}
      disabled={!!dlBusy[docType]}
      style={{
        display: "inline-flex", alignItems: "center", gap: 4,
        background: "transparent", border: "1px solid #2a3a4a", borderRadius: 4,
        color: dlBusy[docType] ? "#4b5563" : "#9ca3af",
        fontSize: 10, fontWeight: 600, padding: "3px 8px",
        cursor: dlBusy[docType] ? "not-allowed" : "pointer",
        opacity: dlBusy[docType] ? 0.6 : 1,
        transition: "color 0.15s, border-color 0.15s",
      }}
      onMouseEnter={(e) => { if (!dlBusy[docType]) { e.currentTarget.style.color = "#e5e7eb"; e.currentTarget.style.borderColor = "#4b5563"; } }}
      onMouseLeave={(e) => { e.currentTarget.style.color = dlBusy[docType] ? "#4b5563" : "#9ca3af"; e.currentTarget.style.borderColor = "#2a3a4a"; }}
    >
      <span style={{ fontSize: 11 }}>{dlBusy[docType] ? "⏳" : "↓"}</span>
      {dlBusy[docType] ? "…" : DOWNLOAD_LABELS[docType]}
    </button>
  );

  // ── Conflict Check ──
  if (stageKey === "conflict") {
    if (status === "done") {
      return (
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span style={{ fontSize: 11, color: DONE_COLOUR, fontWeight: 600 }}>Conflict cleared</span>
          {matter.conflict_cleared_at && (
            <span style={{ fontSize: 10, color: "var(--text-faint)" }}>
              {new Date(matter.conflict_cleared_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}
            </span>
          )}
          {dlBtn("conflict_check")}
        </div>
      );
    }
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ fontSize: 11, color: ACTIVE_COLOUR, fontWeight: 600 }}>Awaiting conflict clearance</span>
        <button
          onClick={(e) => {
            e.stopPropagation();
            openConflictCheck({
              conflictCheckId: matter.conflict_check_id,
              matterRef, axios, apiBase: API, navigate,
            });
          }}
          style={actionBtnStyle("#1a2810", "#4ade80", "#4ade80")}
        >
          Open Conflict Check →
        </button>
      </div>
    );
  }

  // ── OB Sent ──
  if (stageKey === "ob_sent") {
    if (status === "done") {
      // WP-I: surface the email-tracking details here (recipient +
      // delivery) regardless of the matter's current pipeline stage.
      // Previously these fields were only rendered when the stage was
      // "active", which made the audit trail invisible the moment OB
      // was marked complete — a compliance gap.
      return (
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }} data-testid="stepper-ob-sent-done">
          <span style={{ fontSize: 11, color: DONE_COLOUR, fontWeight: 600 }}>
            {/* BM-H266: a matter whose OB came from a linked sibling was
                labelled "OB sent", which is not what happened. No
                questionnaire was sent to this client, and nothing was
                returned outside the app either. The linked case gets its
                own label. */}
            {matter.linked_ob_matter_reference
              ? `OB satisfied via linked matter ${matter.linked_ob_matter_reference}`
              : matter.ob_skipped ? "OB skipped" : "OB sent"}
          </span>
          {matter.ob_sent_at && (
            <span style={{ fontSize: 10, color: "var(--text-faint)" }}>
              {new Date(matter.ob_sent_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}
            </span>
          )}
          {matter.ob_recipient_email && (
            <span style={{ fontSize: 10, color: "var(--text-faint)" }} data-testid="stepper-ob-recipient">
              to {matter.ob_recipient_email}
            </span>
          )}
          {matter.ob_email_delivery && (
            <span style={{ fontSize: 10, color: "var(--text-faint)" }} data-testid="stepper-ob-delivery">
              · {matter.ob_email_delivery}
            </span>
          )}
          {matter.linked_ob_matter_reference && (
            <LinkOBButton
              matterRef={matterRef}
              linkedRef={matter.linked_ob_matter_reference}
              onSuccess={onSuccess}
            />
          )}
          <SkipTermsStatus matter={matter} matterRef={matterRef} onSuccess={onSuccess} />
          {dlBtn("ob_form")}
        </div>
      );
    }
    if (status === "active" || status === "stuck") {
      // 14May2026: pre-Caseminder matters (no matter_ref yet — conflict
      // check still pending or matter-ref minting failed) cannot Send OB:
      // /pipeline/send-ob requires a real matter_ref to look up the OB
      // record. Render an explanatory placeholder instead of a broken
      // Send-OB form that would 404 on submit. Mirrors the isPreCaseminder
      // pattern used in the conflict-check column below.
      const isPreCaseminder = !matterRef || !String(matterRef).trim();
      if (isPreCaseminder) {
        // BM-H (01/09/2026): if the row is stuck in canonical_ref_deferred
        // state (approve honoured but matter_type couldn't be normalised),
        // render the self-service PA picker instead of the static
        // "contact admin" placeholder. Reeken Patel 01/09/2026 hit this.
        // BM-H241 (02/09/2026): render the picker whenever the row has
        // no canonical ref AND we have a CC id to retry against — covers
        // both defer (matter_type unresolvable at approve time) and Phase
        // B provisioning failure (mint attempted, threw, no ref written).
        // BM-H242 (02/09/2026): widened to ANY pre-caseminder row that
        // has a conflict_check_id but no BL. ref. Reeban Patel 02/09/2026
        // — his row had neither flag set (Phase B hung or the CC row was
        // never stamped), so the narrower gate left him on the static
        // placeholder. The assign-practice-area endpoint is idempotent
        // and 409s if a real BL. ref already exists, so exposing the
        // picker on every stuck row is safe.
        const mintNeedsHelp = !!matter.conflict_check_id;
        if (mintNeedsHelp) {
          return (
            <AssignPracticeAreaPicker
              conflictCheckId={matter.conflict_check_id}
              deferReason={
                matter.canonical_ref_defer_reason ||
                (matter.provisioning_status === "failed"
                  ? matter.provisioning_error ||
                    "Matter reference generation failed"
                  : null)
              }
              onSuccess={onSuccess}
            />
          );
        }
        return (
          <div style={{ fontSize: 11, color: "var(--text-muted)", lineHeight: 1.5 }}>
            <div style={{ fontWeight: 600, color: "var(--amber)", marginBottom: 2 }}>
              Matter reference not yet allocated
            </div>
            <div>
              Complete the conflict check (Fee Earner + Practice Area
              required) to mint the matter reference. The Send OB action
              will appear here once the reference exists.
            </div>
          </div>
        );
      }
      // BM-H266 (Vanessa via Liezel, 3 Sep 2026): when the OB has been
      // satisfied by linking to a sibling matter, offering Send OB is
      // wrong. Buildanova Ltd (02 COCO, Himanie Dubois) showed the green
      // "Linked" chip AND the Send Onboarding Questionnaire form at the
      // same time. Vanessa read the "OB Returned Offline" button as a
      // status and reported OB as saying "completed offline" when it had
      // been linked, not completed offline.
      //
      // On a linked matter we therefore render the satisfied state, never
      // the Send OB form, never Skip OB and never Mark OB Returned
      // Offline (a link is not an offline return, and sending a
      // questionnaire to a client whose OB is inherited is wrong). The
      // Unlink affordance stays so a mistaken link is reversible, and the
      // OB pack download stays because the inherited PDFs are the audit
      // evidence for this stage.
      if (matter.linked_ob_matter_reference) {
        return (
          <div
            style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}
            data-testid="stepper-ob-satisfied-by-link"
          >
            <span style={{ fontSize: 11, color: DONE_COLOUR, fontWeight: 600 }}>
              OB satisfied via linked matter {matter.linked_ob_matter_reference}
            </span>
            <LinkOBButton
              matterRef={matterRef}
              linkedRef={matter.linked_ob_matter_reference}
              onSuccess={onSuccess}
            />
            {dlBtn("ob_form")}
          </div>
        );
      }
      // BM-H278 (Vanessa, 4 Sep 2026): "if OB satisfied by 1. returned
      // 2. marked as skipped/linked/returned offline -> remove 'send OB'
      // from the screen, it's confusing and confused". BM-H266 above
      // covers the linked case only. This covers the rest: a returned OB,
      // an OB returned offline, and an OB skipped by either skip writer.
      // The row states what the OB state actually is instead of showing a
      // send form for a questionnaire nobody needs to complete.
      // status is overridden because matter.status here is the pipeline
      // stage, not the client_onboarding status, and the two vocabularies
      // overlap.
      const obWithheld = obSendWithheldReason({ ...matter, status: matter.ob_status });
      if (obWithheld) {
        return (
          <div
            style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}
            data-testid="stepper-ob-satisfied"
          >
            <span style={{ fontSize: 11, color: DONE_COLOUR, fontWeight: 600 }}>
              {obWithheld}
            </span>
            <SkipTermsStatus matter={matter} matterRef={matterRef} onSuccess={onSuccess} />
            {dlBtn("ob_form")}
          </div>
        );
      }
      return (
        <div>
          <SendOBPanel
            matterRef={matterRef}
            existingLink={matter.ob_link}
            sentAt={matter.ob_sent_at}
            recipientEmail={matter.ob_recipient_email}
            onSuccess={onSuccess}
          />
          <div style={{ marginTop: 6, display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
            {/* BM-H3324: AML Policy §7.5, directors only (the server refuses others too) */}
            {isDirector && <SkipOBButton matterRef={matterRef} onSuccess={onSuccess} clientEmail={matter.ob_recipient_email} />}
            <MarkOBReturnedButton matterRef={matterRef} onSuccess={onSuccess} />
            <LinkOBButton
              matterRef={matterRef}
              linkedRef={matter.linked_ob_matter_reference}
              onSuccess={onSuccess}
            />
          </div>
        </div>
      );
    }
    return <span style={{ fontSize: 11, color: "#4b5563" }}>Pending</span>;
  }

  // ── OB Returned ──
  if (stageKey === "ob_returned") {
    if (status === "done" && matter.ob_status_stored === "tob_save_failed") {
      return (
        <div
          style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}
          data-testid={`stepper-tob-not-filed-${matterRef}`}
        >
          <span style={{ fontSize: 11, color: ACTIVE_COLOUR, fontWeight: 600 }}>
            OB returned. Accepted Terms not filed to SharePoint
          </span>
          <RetryTobFilingButton matterRef={matterRef} onSuccess={onSuccess} />
          {dlBtn("ob_form")}
        </div>
      );
    }
    if (status === "done") {
      return (
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span style={{ fontSize: 11, color: DONE_COLOUR, fontWeight: 600 }}>OB returned</span>
          {matter.ob_returned_at && (
            <span style={{ fontSize: 10, color: "var(--text-faint)" }}>
              {new Date(matter.ob_returned_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}
            </span>
          )}
          {dlBtn("ob_form")}
          {dlBtn("audit_log")}
          <AddJointClientButton matterRef={matterRef} onSuccess={onSuccess} />
        </div>
      );
    }
    if (status === "active" || status === "stuck") {
      return (
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span style={{ fontSize: 11, color: ACTIVE_COLOUR, fontWeight: 600 }}>Awaiting OB return from client</span>
          <AddJointClientButton matterRef={matterRef} onSuccess={onSuccess} />
        </div>
      );
    }
    return <span style={{ fontSize: 11, color: "#4b5563" }}>Pending</span>;
  }

  // ── AML / Thirdfort ──
  // BM-H270: this stage had no branch at all, so the row rendered a bare
  // label and the fee earner had no way to see WHY a linked matter had
  // stopped here. It now says one of three things in plain words: AML was
  // inherited from a named sibling matter and when the underlying check was
  // actually completed, AML is complete on this matter, or AML is still
  // outstanding. A linked matter never gets offered the manual AML actions,
  // for the same reason BM-H266 stopped offering Send OB on a linked row.
  //
  // BM-H274: the outstanding case was still too coarse. A matter whose
  // Thirdfort report had merely arrived was drawn exactly like one where AML
  // was satisfied. Vanessa's ruling is that the fee earner must read and
  // approve the report, so the outstanding case now names the true sub-state
  // from matter.thirdfort_report_state, including "report received, awaiting
  // fee earner review" as a state visibly distinct from AML satisfied, and a
  // refused report as a stop rather than a blank. The inherited case above is
  // untouched: BM-H270 only ever inherits an AML the shared aml_satisfied
  // predicate accepted, and since BM-H274 that means an APPROVED report.
  if (stageKey === "aml_check") {
    const inheritedFrom = matter.aml_inherited_from;
    const completedAt = matter.aml_original_completed_at;
    const reportState = matter.thirdfort_report_state || "";
    const completedLabel = completedAt
      ? new Date(completedAt).toLocaleDateString("en-GB", {
          day: "numeric", month: "short", year: "numeric",
        })
      : null;

    if (inheritedFrom) {
      return (
        <div
          style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}
          data-testid="stepper-aml-satisfied-by-link"
        >
          <span style={{ fontSize: 11, color: DONE_COLOUR, fontWeight: 600 }}>
            AML satisfied via linked matter {inheritedFrom}
          </span>
          {completedLabel && (
            <span
              style={{ fontSize: 10, color: "var(--text-faint)" }}
              data-testid="stepper-aml-original-completed-at"
            >
              completed {completedLabel}
            </span>
          )}
          {matter.aml_inherited_stale && (
            <span
              style={{ fontSize: 10, color: STUCK_COLOUR, fontWeight: 600 }}
              data-testid="stepper-aml-inherited-stale"
              title="The inherited check is older than the firm's re-verification period. Consider a fresh check."
            >
              re-verification due
            </span>
          )}
          <LinkOBButton
            matterRef={matterRef}
            linkedRef={inheritedFrom}
            onSuccess={onSuccess}
          />
          {dlBtn("audit_log")}
        </div>
      );
    }

    if (status === "done") {
      return (
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }} data-testid="stepper-aml-done">
          <span
            style={{ fontSize: 11, color: DONE_COLOUR, fontWeight: 600 }}
            data-testid={`pipeline-aml-done-${matterRef}`}
          >
            {reportState === "approved"
              ? "AML complete, report approved by fee earner"
              : "AML complete"}
          </span>
          {completedLabel && (
            <span style={{ fontSize: 10, color: "var(--text-faint)" }}>{completedLabel}</span>
          )}
          {reportState === "approved" && matter.thirdfort_report_reviewed_at && (
            <span style={{ fontSize: 10, color: "var(--text-faint)" }}>
              {new Date(matter.thirdfort_report_reviewed_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}
              {matter.thirdfort_report_reviewed_by ? ` by ${matter.thirdfort_report_reviewed_by}` : ""}
            </span>
          )}
          {dlBtn("audit_log")}
        </div>
      );
    }

    if (status === "active" || status === "stuck") {
      // BM-H274. Ordered most specific first. The default is BM-H270's
      // original wording, so a linked matter with nothing to inherit still
      // reads exactly as it did.
      const line =
        reportState === "awaiting_fee_earner_review"
          ? "Report received, awaiting fee earner review"
          : reportState === "rejected"
          ? "Report rejected by fee earner, AML outstanding"
          : reportState === "referred"
          ? "Report referred by fee earner, AML outstanding"
          : reportState === "awaiting_report"
          ? "Thirdfort complete, awaiting report"
          : reportState === "awaiting_client"
          ? "Thirdfort sent, awaiting client"
          : "AML / identity check outstanding";
      const declined = reportState === "rejected" || reportState === "referred";
      const awaitingReview = reportState === "awaiting_fee_earner_review";
      return (
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }} data-testid="stepper-aml-outstanding">
          <span
            style={{ fontSize: 11, color: declined ? "#f59e0b" : ACTIVE_COLOUR, fontWeight: 600 }}
            data-testid={`pipeline-aml-state-${matterRef}`}
          >
            {line}
          </span>
          {/* BM-H466 (Vanessa, 9 Sep 2026): when the link worked out exactly
              why the AML could not be carried, say that, in the words the
              backend already chose. This is the BL.VEC.006.1507 case, where
              the matter stopped at this stage and no screen said so. */}
          {matter.aml_inherit_skip_reason && !matter.aml_satisfied && (
            <span
              style={{ fontSize: 10, color: STUCK_COLOUR, fontWeight: 600 }}
              data-testid="stepper-aml-inherit-refused"
              title={matter.aml_inherit_skip_reason}
            >
              AML did not carry across from the linked matter
            </span>
          )}
          {matter.aml_inherit_skip_reason && !matter.aml_satisfied && (
            <span
              style={{ fontSize: 10, color: "var(--text-muted)" }}
              data-testid="stepper-aml-inherit-refused-reason"
            >
              {matter.aml_inherit_skip_reason}
            </span>
          )}
          {/* BM-H466: this hint has been dead code since BM-H274. The guard
              was `!reportState`, but thirdfort_report_state never returns an
              empty string: its floor value is the truthy "no_thirdfort". So
              the hint rendered in its own unit test, whose fixture omitted the
              field, and never once on a real matter. It is now shown as the
              fallback for a linked matter that has no recorded reason of its
              own, which is every matter linked before this change shipped. */}
          {matter.linked_ob_matter_reference
            && !matter.linked_aml_matter_reference
            && !matter.aml_inherit_skip_reason
            && (!reportState || reportState === "no_thirdfort") && (
            <span
              style={{ fontSize: 10, color: "var(--text-faint)" }}
              data-testid="stepper-aml-not-inheritable"
            >
              nothing to inherit from {matter.linked_ob_matter_reference}
            </span>
          )}
          {declined && matter.thirdfort_report_review_notes && (
            <span style={{ fontSize: 10, color: "var(--text-faint)" }}>{matter.thirdfort_report_review_notes}</span>
          )}
          {/* BM-H382: AML is linked as its own step. This stage is about
              identity verification, so it carries the AML control, not the
              onboarding one. It is offered unconditionally — a matter does
              not have to have a linked OB to inherit an identity check, and
              the two halves may point at different earlier matters.

              BM-H282 still applies: a link inherits only what the source
              matter held at link time, so when that matter's report is
              approved later the link has to be re-run. That is what
              "Refresh AML" on the linked pill does. */}
          <LinkAMLButton
            matterRef={matterRef}
            linkedRef={matter.linked_aml_matter_reference}
            onSuccess={onSuccess}
            onGoToForf={() => navigate(`/forf/${encodeURIComponent(matterRef)}?step=risk`)}
          />
          {/* BM-H577: the source matter's AML has since been completed, so
              the refusal shown above is out of date and there is something
              to carry across. Offered only on the backend's confirmation
              against the source record, never on a guess. */}
          {matter.aml_inherit_refreshable && !matter.aml_satisfied && (
            <RefreshAMLInheritanceButton
              matterRef={matterRef}
              sourceRef={
                matter.linked_aml_matter_reference
                || matter.aml_inherited_from
                || matter.linked_ob_matter_reference
              }
              onSuccess={onSuccess}
            />
          )}
          {awaitingReview ? (
            <button
              onClick={(e) => { e.stopPropagation(); navigate(`/matter/${encodeURIComponent(matterRef)}#thirdfort-review`); }}
              data-testid={`pipeline-review-thirdfort-${matterRef}`}
              title="Open the matter to read and approve the Thirdfort report"
              style={{
                display: "inline-flex", alignItems: "center", gap: 4,
                background: "transparent", border: "1px solid var(--brand-line)",
                borderRadius: 4, color: "var(--brand-text)",
                fontSize: 10, fontWeight: 600, padding: "3px 8px", cursor: "pointer",
              }}
            >
              Review report
            </button>
          ) : (
            <button
              onClick={(e) => { e.stopPropagation(); navigate(`/matter/${encodeURIComponent(matterRef)}#thirdfort-review`); }}
              style={actionBtnStyle("#1a2810", "#4ade80", "#4ade80")}
            >
              Open AML / Thirdfort →
            </button>
          )}
        </div>
      );
    }
    return <span style={{ fontSize: 11, color: "#4b5563" }}>Pending</span>;
  }

  // ── FORF Draft ──
  if (stageKey === "forf_draft") {
    if (status === "done") {
      // Even when FORF is fully submitted, expose a path back to /forf/{ref}
      // so any signed-in user can hit the "Revert to Draft" button there.
      // Without this link, matters that have advanced to ccl_draft / pre_open
      // are routed to /pre-caseminder?tab=ccl and the FORF page is unreachable.
      return (
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span style={{ fontSize: 11, color: DONE_COLOUR, fontWeight: 600 }}>FORF complete</span>
          {dlBtn("forf")}
          <button
            onClick={(e) => { e.stopPropagation(); navigate(`/forf/${encodeURIComponent(matterRef)}`); }}
            data-testid={`pipeline-edit-forf-${matterRef}`}
            title="Open the FORF page (revert to draft to edit fields)"
            style={{
              display: "inline-flex", alignItems: "center", gap: 4,
              background: "transparent", border: "1px solid #b45309",
              borderRadius: 4, color: "#f59e0b",
              fontSize: 10, fontWeight: 600, padding: "3px 8px",
              cursor: "pointer",
            }}
          >
            ↺ Edit FORF
          </button>
        </div>
      );
    }
    // BM-H466 (Vanessa, 9 Sep 2026): the AML gate can withhold the draft file
    // opening request, and until now this cell said "Pending" or nothing at
    // all. No FORF means no CMRA, and the CDD plus CMRA rule then locks the
    // file, so this is the cell where the delay actually shows up. The gate is
    // correct and untouched; the wording is the gate's own, read from the
    // backend and never restated here.
    if (status !== "done" && matter.forf_withheld_reason) {
      return (
        <div
          style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}
          data-testid="stepper-forf-withheld"
        >
          <span
            style={{ fontSize: 11, color: STUCK_COLOUR, fontWeight: 600 }}
            title={matter.forf_withheld_reason}
          >
            File opening request on hold
          </span>
          <span
            style={{ fontSize: 10, color: "var(--text-muted)" }}
            data-testid="stepper-forf-withheld-reason"
          >
            {matter.forf_withheld_reason}
          </span>
        </div>
      );
    }
    if (status === "active" || status === "stuck") {
      return (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 11, color: ACTIVE_COLOUR, fontWeight: 600 }}>Ready for FORF</span>
          <button
            onClick={(e) => { e.stopPropagation(); navigate(`/forf/${encodeURIComponent(matterRef)}?step=risk`); }}
            data-testid={`pipeline-go-to-forf-${matterRef}`}
            style={{
              ...ghostBtnStyle,
              background: "var(--brand-dim)", border: "1px solid var(--brand-line)",
              color: "var(--brand-text)", padding: "5px 12px", fontSize: 11,
              fontWeight: 600, borderRadius: 5,
            }}
          >
            {/* BM-H670: named for what it opens, both sections. */}
            Go to FORF &amp; Risk Assessment →
          </button>
        </div>
      );
    }
    return <span style={{ fontSize: 11, color: "#4b5563" }}>Pending</span>;
  }

  // ── CCL Draft ──
  if (stageKey === "ccl_draft") {
    if (status === "done") {
      return (
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span style={{ fontSize: 11, color: DONE_COLOUR, fontWeight: 600 }}>CCL drafted</span>
          {dlBtn("ccl")}
        </div>
      );
    }
    if (status === "active" || status === "stuck") {
      return (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 11, color: ACTIVE_COLOUR, fontWeight: 600 }}>CCL draft in progress</span>
          <button
            onClick={(e) => {
              e.stopPropagation();
              // 20 May 2026 (Vanessa): was hard-coded to /pre-caseminder
              // (the intake form) which had no CCL editor. The real CCL
              // editor lives on the matter's submission detail page;
              // openMatterCcl resolves matter_ref → submission_id.
              openMatterCcl({ matterRef, axios, apiBase: API, navigate });
            }}
            style={actionBtnStyle("#1a2230", "#60a5fa", "#60a5fa")}
            data-testid="open-ccl-btn"
          >
            Open CCL →
          </button>
        </div>
      );
    }
    return <span style={{ fontSize: 11, color: "#4b5563" }}>Pending</span>;
  }

  // ── CCL Signed ──
  if (stageKey === "ccl_signed") {
    // BM-H503 (Vanessa, 10 Sep 2026 19:00): "this should be marked by the
    // system, not us." A recorded client signature is read here directly and
    // outranks the stage the tracker happens to be carrying. Before this, the
    // row decided "signed" versus "awaiting signature" purely from that stage,
    // which is synced separately after the signature is stored; when the sync
    // failed - as it did on BL.HD.008.1527 on 11 Sep 2026 - the row asked staff
    // to mark a signature the client had already given. It never asks that
    // again. The manual override below is untouched and still renders for a
    // matter with no recorded signature (phone / reply-email confirmation,
    // Audit 05 P1-2).
    if (matter.ccl_signature_recorded === true && status !== "done") {
      return (
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span
            style={{ fontSize: 11, color: DONE_COLOUR, fontWeight: 600 }}
            data-testid="ccl-signed-self-detected"
          >
            CCL signed by client
          </span>
          {/* Say plainly that the internal tracker is catching up, rather than
              showing a call to action on a matter that is already signed. The
              tracker repairs itself on the next read (advance_pipeline
              transition 4 is self-healing as of BM-H503). */}
          <span style={{ fontSize: 10, color: "#8296b0" }}>
            signed, syncing — no action needed
          </span>
          {dlBtn("ccl")}
        </div>
      );
    }
    if (status === "done") {
      return (
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span style={{ fontSize: 11, color: DONE_COLOUR, fontWeight: 600 }}>CCL signed — matter open</span>
          {dlBtn("ccl")}
        </div>
      );
    }
    if (status === "active" || status === "stuck") {
      return (
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span style={{ fontSize: 11, color: ACTIVE_COLOUR, fontWeight: 600 }}>Awaiting CCL signature</span>
          {/* BM-H3443: say the letter went by hand, so nobody sends it twice. */}
          {matter.ccl_sent_manually && (
            <span style={{ fontSize: 10, color: "#8296b0" }} data-testid="ccl-sent-manually-note">
              {matter.ccl_sent_manually}
            </span>
          )}
          {/*
            Audit 05 P1-2 (01/09/2026): the isDirector gate here was
            preventing owning fee earners from advancing their own
            matters when the client confirmed CCL by phone or reply
            email. Backend now enforces the widened policy (ADMIN,
            DIRECTOR, or owning fee earner), so we surface the button
            for all staff and let a 403 come back if someone else on
            the team clicks it on a matter they don't own.
          */}
          {/* BM-H494: no positioning context needed now the panel is in flow.
              minWidth 0 lets this flex item shrink, and the row wraps, so the
              panel takes the width available instead of overflowing sideways. */}
          <div style={{ minWidth: 0, maxWidth: "100%" }}>
            <MarkSignedButton matterRef={matterRef} onSuccess={onSuccess} />
          </div>
        </div>
      );
    }
    return <span style={{ fontSize: 11, color: "#4b5563" }}>Pending</span>;
  }

  return null;
}

// ─── pipelineStageStatus ─────────────────────────────────────────────────────
// One answer to "what state is this stage in" for both renderers, the summary
// row's pills and the expanded stepper. It used to be written out twice, so a
// correction to one could leave the other drawing the old answer.
//
// BM-H284 (Vanessa, 4 Sep 2026). The AML dot must not be lit by stage
// order. Every other stage on this stepper is a workflow step, and having
// moved past one is fair evidence it happened. AML is not a workflow step,
// it is an SRA-regulated identity control, and having moved past it proves
// only that somebody moved past it. On BL.VEC.006.1302 this rendered green
// while the review panel on the same screen said the check had not come
// back. matter.aml_satisfied carries the same shared predicate as the gate,
// so an approved report, an explicit human confirmation, a legacy aml_status
// and kyc_complete all light it, and a declined report does not.
//
// BM-H503 (Vanessa, 10 Sep 2026 19:00): "this should be marked by the system,
// not us." A recorded client signature makes CCL Signed done whatever stage
// the separately-synced tracker is carrying, so a failed sync can never
// present a signed matter as awaiting signature.
export function pipelineStageStatus(matter, key) {
  const order      = STAGES.map((s) => s.key);
  const bottleneck = matter.bottleneck_stage || "conflict";
  const completed  = matter.completed_stages || [];
  const amlHonest = matter.aml_satisfied === true;
  const cclSignedHonest = matter.ccl_signature_recorded === true;
  if (key === "ccl_signed" && cclSignedHonest) return "done";
  if (key === "aml_check") {
    if (amlHonest) return "done";
    return key === bottleneck && matter.is_stuck ? "stuck"
      : key === bottleneck ? "active"
      : order.indexOf(key) < order.indexOf(bottleneck) ? "stuck"
      : "todo";
  }
  if (completed.includes(key)) return "done";
  // BM-H3314: OB back and AML done, so the FORF is the live step even though
  // the row is still filed under OB Back. See adaptItem.
  if (key === "forf_draft" && matter.ready_for_forf) {
    return matter.is_stuck ? "stuck" : "active";
  }
  if (order.indexOf(key) < order.indexOf(bottleneck)) return "done";
  if (key === bottleneck) return matter.is_stuck ? "stuck" : "active";
  return "todo";
}

// ─── AccordionDetail (Vertical Stepper) ──────────────────────────────────────
function AccordionDetail({ matter, navigate, onSuccess, isDirector }) {
  const bottleneck = matter.bottleneck_stage || "conflict";
  // BM-H284 / BM-H503: see pipelineStageStatus above.
  const stageStatus = (key) => pipelineStageStatus(matter, key);

  return (
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        borderTop: "1px solid var(--navy-border)",
        background: "#060d18",
        padding: "18px 20px 14px",
      }}
    >
      {/* BM-H3368: the next step in full when the row is opened. */}
      {matter.next_step && (
        <NextStepBanner matterRef={matter.matter_ref} status={{ next_step: matter.next_step }} />
      )}
      {/* BM-H285: say plainly where this stage came from. */}
      <div
        style={{
          fontSize: 11,
          marginBottom: 10,
          color: matter.stage_is_explicit ? "#7fd1b9" : "var(--navy-muted, #8296b0)",
        }}
      >
        {matter.stage_is_explicit
          ? `Stage recorded by a person${matter.stage_recorded ? `: ${matter.stage_recorded}` : ""}`
          : "Stage inferred from file state, not set by a person"}
      </div>

      {/* Vertical Stepper */}
      <div style={{ display: "flex", flexDirection: "column", position: "relative" }}>
        {STAGES.map((stage, i) => {
          const status = stageStatus(stage.key);
          const isLast = i === STAGES.length - 1;
          return (
            <div key={stage.key} style={{ display: "flex", gap: 14, minHeight: 42, position: "relative" }}>
              {/* Dot + vertical line column */}
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: 14 }}>
                <StepperDot status={status} />
                {!isLast && (
                  <div style={{
                    width: 2, flex: 1, minHeight: 12,
                    background: status === "done" ? DONE_COLOUR + "60" : "#1e293b",
                  }} />
                )}
              </div>
              {/* Content column */}
              <div style={{ flex: 1, paddingBottom: isLast ? 0 : 10, minWidth: 0 }}>
                <div style={{
                  fontSize: 11, fontWeight: 700, marginBottom: 4,
                  color: status === "done" ? DONE_COLOUR
                       : status === "active" ? ACTIVE_COLOUR
                       : status === "stuck" ? STUCK_COLOUR
                       : "#4b5563",
                  letterSpacing: "0.03em",
                }}>
                  {status === "done" && stage.doneLabel ? stage.doneLabel : stage.label}
                </div>
                <StepperStageContent
                  stageKey={stage.key}
                  status={status}
                  matter={matter}
                  navigate={navigate}
                  onSuccess={onSuccess}
                  isDirector={isDirector}
                />
                {/* Phase 2d-ii — surface existing per-matter email read-receipts
                    inside the OB stage of the live pipeline accordion. The
                    ReadReceipts component handles its own fetch, errors and
                    null-render when there are no tracked emails. */}
                {stage.key === "ob_sent" && matter.matter_ref && (
                  <div style={{ marginTop: 8 }} data-testid="pipeline-ob-read-receipts">
                    <ReadReceipts matterReference={matter.matter_ref} compact />
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer: days at stage + Open Full Matter + Delete */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        borderTop: "1px solid var(--navy-border)", paddingTop: 12, marginTop: 10, flexWrap: "wrap", gap: 8,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          {matter.days_in_stage != null && (
            <span style={{ fontSize: 11, color: daysColour(matter.days_in_stage), fontWeight: 600 }}>
              {matter.days_in_stage}d at current stage
            </span>
          )}
          <button
            onClick={() => {
              if (CCL_STAGE_KEYS.has(bottleneck)) {
                openMatterCcl({
                  matterRef: matter.matter_ref,
                  axios, apiBase: API, navigate,
                });
                return;
              }
              if (bottleneck === "conflict") {
                openConflictCheck({
                  conflictCheckId: matter.conflict_check_id,
                  matterRef: matter.matter_ref,
                  axios, apiBase: API, navigate,
                });
                return;
              }
              navigate(STAGE_ROUTES[bottleneck]?.(matter.matter_ref) || `/pipeline`);
            }}
            style={{ ...ghostBtnStyle, fontSize: 11 }}
          >
            Open Full Matter →
          </button>
        </div>
        {isDirector && (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {(matter.matter_ref || matter.provisional_id) && (
              <SetStageOverride matterRef={matter.matter_ref || matter.provisional_id} currentStage={matter.backend_stage} onSuccess={onSuccess} />
            )}
            <DeleteMatterButton
              matterRef={matter.matter_ref}
              provisionalId={matter.provisional_id}
              conflictCheckId={matter.conflict_check_id}
              onDeleted={onSuccess}
            />
          </div>
        )}
      </div>

      {/* Download bar at the bottom */}
      <DownloadBar matterRef={matter.matter_ref} bottleneckStage={bottleneck} />

      {/* WP-I — persistent, stage-agnostic audit trail.  Rendered for EVERY
          matter regardless of pipeline stage so email-tracking events and
          other compliance-critical history stay visible after OB returns,
          CCL is sent, etc. */}
      {/* Email delivery audit — every email tied to the matter (internal + external)
          with per-email Sent/Delivered/Bounced/Opened icons + header summary pills.
          Single source of truth: GET /email-audit/delivery/{matter_ref}.
          Delivered = delivered to CLIENT (no NDR within bounce-quiet window),
          NOT delivered to Microsoft (Vanessa 19 May 2026). */}
      {matter.matter_ref && (
        <div style={{ marginTop: 12 }} data-testid="pipeline-email-delivery-audit">
          <EmailDeliveryAudit matterReference={matter.matter_ref} />
        </div>
      )}
      {matter.matter_ref && (
        <div data-testid="pipeline-lawbillity-sync-status">
          <LawbillitySyncStatus matterReference={matter.matter_ref} isDirector={isDirector} />
        </div>
      )}
      {matter.matter_ref && (
        <div style={{ marginTop: 12 }}>
          <MatterAuditAccordion matterReference={matter.matter_ref} />
        </div>
      )}
    </div>
  );
}

function SectionHeading({ children }) {
  return (
    <div style={{ fontSize: 10, fontWeight: 700, color: "var(--green)", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 10 }}>
      {children}
    </div>
  );
}

// ─── PipelineRow ──────────────────────────────────────────────────────────────
function PipelineRow({ matter, navigate, onSuccess, isDirector, showMoneyBar, financials, credit, selectedCcIds, onToggleCcSelection, autoExpand }) {
  const [open, setOpen] = useState(!!autoExpand);
  // Auto-expand when autoExpand prop transitions from false to true (after onSuccess refetch)
  useEffect(() => { if (autoExpand) setOpen(true); }, [autoExpand]);
  const m               = matter;
  const bottleneck      = m.bottleneck_stage || "conflict";
  const rag             = m.rag || "amber";
  const ragColour       = HEALTH_COLOURS[rag] || HEALTH_COLOURS.unknown;
  const conflictCleared = bottleneck !== "conflict";
  // Pre-CaseMinder matters: stuck at conflict stage with no matter_reference
  const isPreCaseminder = bottleneck === "conflict" && !m.matter_ref && !!m.conflict_check_id;
  const isSelected      = isPreCaseminder && selectedCcIds && selectedCcIds.has(m.conflict_check_id);

  // BM-H284: same answer as the stepper below, from the one shared function,
  // so the state never depends on which view you happen to be looking at.
  const stageStatus = (key) => pipelineStageStatus(m, key);

  const dc = daysColour(m.days_in_stage);
  const hasFinancial = showMoneyBar && financials && (financials.money_on_account > 0 || financials.wip > 0);

  return (
    <div style={{ borderBottom: "1px solid var(--navy-border)" }}>
      {/* summary row */}
      <div
        onClick={() => setOpen((v) => !v)}
        style={{
          display: "grid",
          gridTemplateColumns: "24px minmax(160px,2fr) 120px 1fr 64px 60px 44px",
          alignItems: "center",
          gap: 10,
          padding: "11px 16px",
          borderLeft: `3px solid ${ragColour}`,
          // BM-H246 (Audit 09 F-06): amber outline on stuck rows so
          // top-down pipeline scans surface no-ref-after-approve rows
          // that would otherwise show GREEN by days_in_stage.
          boxShadow: m.stuck ? "inset 0 0 0 1px var(--amber)" : "none",
          cursor: "pointer",
          transition: "background 0.12s",
          background: open ? "#0c1a28" : "transparent",
        }}
        onMouseEnter={(e) => { if (!open) e.currentTarget.style.background = "#0f1e2d"; }}
        onMouseLeave={(e) => { if (!open) e.currentTarget.style.background = "transparent"; }}
      >
        {isPreCaseminder && onToggleCcSelection ? (
          <div style={{ textAlign: "center" }} onClick={(e) => { e.stopPropagation(); onToggleCcSelection(m.conflict_check_id); }}>
            <input
              type="checkbox"
              checked={!!isSelected}
              onChange={() => {}}
              onClick={(e) => e.stopPropagation()}
              title="Select for bulk clear (pre-CaseMinder reviewed)"
              style={{ cursor: "pointer", accentColor: "var(--amber)" }}
            />
          </div>
        ) : (
          <div style={{ color: "var(--text-faint)", fontSize: 12, transition: "transform 0.2s", transform: open ? "rotate(90deg)" : "rotate(0deg)", textAlign: "center" }}>▶</div>
        )}

        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            {/* BM-H3775: the name is white; green is for status. */}
            <div style={{ color: "var(--text)", fontWeight: 600, fontSize: 13 }}>{m.client_display_name || m.client_name || "—"}</div>
            {m.matter_ref && (
              <CorrectClientDetailsButton
                matterRef={m.matter_ref}
                clientName={m.client_name}
                initial={{
                  email: m.client_email || m.primary_client?.email || "",
                  phone: m.client_phone || m.primary_client?.phone || "",
                  mobile: m.client_mobile || m.primary_client?.mobile || "",
                  address: m.client_address || m.primary_client?.address || "",
                }}
                onSaved={onSuccess}
                testId="pipeline-row-correct-client-btn"
                iconClassName="w-3 h-3"
              />
            )}
          </div>
          <div style={{ color: "var(--text-muted)", fontSize: 11, marginTop: 2 }}>
            {m.matter_ref}
            {m.matter_type && <span style={{ marginLeft: 6, color: "var(--text-faint)" }}>· {m.matter_type}</span>}
          </div>
          {m.next_step && (
            <NextStepBanner compact matterRef={m.matter_ref} status={{ next_step: m.next_step }} />
          )}
        </div>

        <div style={{ color: "var(--text-muted)", fontSize: 12 }}>{m.fee_earner_name || "—"}</div>

        {/* BM-H3775: one line of progress, not seven pills, here and on the
            dashboard (Vanessa, 6 Oct 2026). A segment opens its stage. */}
        <PipelineStageTrack
          stages={STAGES}
          statuses={Object.fromEntries(STAGES.map((s) => [s.key, stageStatus(s.key)]))}
          onOpen={(key) => openStageScreen(key, { matterRef: m.matter_ref, conflictCheckId: m.conflict_check_id, navigate,
                                                   preCm: m.pre_caseminder_registered ? m : null })}
        />

        <div style={{ textAlign: "center" }}>
          {m.days_in_stage != null ? (
            <span style={{
              display: "inline-block",
              background: dc + "22",
              border: `1px solid ${dc}`,
              color: dc,
              borderRadius: 4,
              padding: "2px 7px",
              fontSize: 11,
              fontWeight: 700,
              fontVariantNumeric: "tabular-nums",
            }}>
              {m.days_in_stage}d
            </span>
          ) : (
            <span style={{ color: "var(--text-faint)", fontSize: 11 }}>—</span>
          )}
        </div>

        <div>
          {/* BM-H3775: a dot, not a bordered pill; the row's left edge repeats it. */}
          <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 10, fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
            <span style={{ width: 7, height: 7, borderRadius: 99, background: ragColour }} />
            {rag}
          </span>
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 6, alignItems: "center" }}>
          {conflictCleared && !m.ob_sent_at && (
            <span title="Conflict clear — OB ready to send (click to expand)" style={{ fontSize: 16, cursor: "pointer" }}>📨</span>
          )}
          {/* BM-H3314: this chip looked like a link and was a label, and it
              showed on an OB-back matter whose AML was still outstanding. It
              now appears only when the FORF is genuinely the next step, and
              it opens the FORF. */}
          {m.ready_for_forf && m.matter_ref && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); navigate(`/forf/${encodeURIComponent(m.matter_ref)}?step=risk`); }}
              title="OB back and AML complete: open the FORF & Risk Assessment"
              data-testid={`pipeline-row-forf-${m.matter_ref}`}
              style={{
                fontSize: 9, fontWeight: 700, color: "#34d399", background: "#064e3b",
                border: "1px solid #059669", padding: "2px 6px", borderRadius: 4,
                textTransform: "uppercase", letterSpacing: "0.05em", cursor: "pointer",
              }}
            >
              FORF →
            </button>
          )}
        </div>
      </div>

      {/* MoneyBar + CreditChip — between summary row and accordion */}
      {showMoneyBar && (
        <div style={{ padding: "0 16px 6px 43px", display: "flex", alignItems: "center", gap: "12px" }}>
          <div style={{ flex: 1 }}>
            <MoneyBar
              quote={financials?.quote || 0}
              wip={financials?.wip || 0}
              moneyOnAccount={financials?.money_on_account || 0}
              matterRef={matter.matter_ref}
              compact
            />
          </div>
          {credit && (credit.total_outstanding > 0 || credit.escalation_stage) && (
            <CreditChip
              invoiceDate={credit.invoice_date}
              totalOutstanding={credit.total_outstanding}
              escalationStage={credit.escalation_stage}
              daysOverdue={credit.days_overdue}
            />
          )}
        </div>
      )}

      {open && (
        <AccordionDetail
          matter={m}
          navigate={navigate}
          onSuccess={onSuccess}
          isDirector={isDirector}
        />
      )}
    </div>
  );
}

// ─── FilterChip ───────────────────────────────────────────────────────────────
function FilterChip({ label, onRemove }) {
  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "#0f1e2d", border: "1px solid #4ade80", borderRadius: 99, padding: "3px 10px", fontSize: 11, color: "var(--green)", fontWeight: 600 }}>
      {label}
      <span onClick={onRemove} style={{ cursor: "pointer", opacity: 0.7, fontSize: 13, lineHeight: 1 }} title="Remove filter">×</span>
    </div>
  );
}

// ─── SummaryBar ───────────────────────────────────────────────────────────────
function SummaryBar({ summary, stageFilter, setStageFilter }) {
  const cards = [
    { key: "all",        label: "In Flight",      value: summary.total_in_flight,   colour: "#e5e7eb" },
    { key: "conflict",   label: "At: Conflict", value: summary.stuck_at_conflict, colour: "#ef4444" },
    { key: "ob_sent",    label: "At: OB",       value: summary.stuck_at_ob,       colour: "#f59e0b" },
    { key: "ccl_draft",  label: "At: CCL",      value: summary.stuck_at_ccl,      colour: "#f59e0b" },
    { key: "ccl_signed", label: "CCL Signed (30 days)", value: summary.ready_to_open, colour: "#1aab6e",
      testid: "summary-ccl-signed-30-days", readOnly: true },
    // ── Section 4 §3.7 — CDD renewal KPI cells ───────────────────
    // Visibility-only counters at the end of the summary row. Colour:
    // red when >0 overdue (SRA-defensible day one), amber when >0
    // due_soon. Click behaviour is a stage-filter no-op for now — the
    // matter-list filter param isn't wired (Section 4 §3.9 explicit
    // ack: "ship as counters and flag in PR_DESCRIPTION.md if the
    // filter param doesn't exist"). Counters remain SRA-screenshotable.
    { key: "cdd_overdue",  label: "Renewals overdue",
      value: summary.cdd_renewals_overdue,
      colour: summary.cdd_renewals_overdue > 0 ? "#ef4444" : "#7c8aa3",
      testid: "summary-cdd-renewals-overdue", readOnly: true },
    { key: "cdd_due_soon", label: "Renewals due soon",
      value: summary.cdd_renewals_due_soon,
      colour: summary.cdd_renewals_due_soon > 0 ? "#f59e0b" : "#7c8aa3",
      testid: "summary-cdd-renewals-due-soon", readOnly: true },
  ];
  return (
    <div style={{ display: "flex", gap: 12, marginBottom: 20, flexWrap: "wrap" }}>
      {cards.map((c) => (
        <div
          key={c.key}
          data-testid={c.testid}
          onClick={() => { if (!c.readOnly) setStageFilter(stageFilter === c.key ? "all" : c.key); }}
          style={{
            flex: "1 1 130px", background: stageFilter === c.key ? "#0f1e2d" : "#0a1628",
            border: `1px solid ${stageFilter === c.key ? c.colour : "var(--navy-border)"}`,
            borderRadius: 8, padding: "12px 16px",
            cursor: c.readOnly ? "default" : "pointer",
            transition: "border-color 0.15s, background 0.15s",
          }}
        >
          <div style={{ fontSize: 24, fontWeight: 700, color: c.colour, fontVariantNumeric: "tabular-nums" }}>{c.value ?? 0}</div>
          <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>{c.label}</div>
        </div>
      ))}
    </div>
  );
}

// ─── shared micro-styles ──────────────────────────────────────────────────────
const actionBtnStyle = (bg, border, color) => ({
  background: bg, border: `1px solid ${border}`, borderRadius: 4,
  color, fontSize: 11, fontWeight: 700, padding: "3px 8px",
  cursor: "pointer", whiteSpace: "nowrap", letterSpacing: "0.02em", transition: "opacity 0.15s",
});
const ghostBtnStyle = {
  background: "transparent", border: "1px solid #374151", borderRadius: 5,
  color: "var(--text-muted)", fontSize: 11, padding: "5px 12px", cursor: "pointer",
};
const cancelBtnStyle = {
  background: "transparent", border: "1px solid #374151", borderRadius: 4,
  color: "var(--text-muted)", fontSize: 11, padding: "4px 10px", cursor: "pointer",
};
const confirmBtnStyle = {
  border: "none", borderRadius: 4, color: "#fff",
  fontSize: 11, fontWeight: 700, padding: "4px 12px", cursor: "pointer",
};
const textareaStyle = {
  width: "100%", background: "var(--navy)", border: "1px solid var(--navy-border)",
  borderRadius: 4, color: "var(--text)", fontSize: 12, padding: "6px 8px",
  resize: "vertical", marginBottom: 8, fontFamily: "inherit",
  outline: "none", boxSizing: "border-box",
};
const obDoneStyle = {
  display: "flex", alignItems: "center", gap: 8,
  background: "var(--navy)", border: "1px solid #1aab6e",
  borderRadius: 8, padding: "10px 14px", marginTop: 12,
};

// ─── Sortable column headers (14 May 2026, Liezel's ask) ────────────────────
//
// Liezel: "How are the matters in the pipeline arranged? Would it be
// possible to have the newest matters appear at the top?"
//
// Pre-fix the rows rendered in whatever order the backend handed them
// over (auto-cleared matters tended to land last, so the freshest work
// was at the bottom). Vanessa originally specced sortable headers
// weeks ago; this restores them.
//
// Design notes:
//   * The pipeline view is a CSS-grid header + accordion rows — not a
//     <table>. Click targets are the existing 7-column grid cells.
//   * No backend change — 107 visible rows means client-side sort is
//     trivially fast and avoids breaking the cached /pipeline/summary
//     contract.
//   * Default sort is { column: "created_at", dir: "desc" } —
//     newest-first, Liezel's actual ask.
//   * User's sort choice persists to localStorage under
//     PIPELINE_SORT_STORAGE_KEY so it survives reloads.
//   * Nulls sort to the BOTTOM regardless of direction — never silently
//     to the top or middle. The comparator explicitly comments this so
//     a future refactor can't quietly invert it.
//
// The columns sortable from the header row:
//   client   — Client / Ref (text)
//   fee_earner — Fee Earner (text)
//   stage    — Stages / bottleneck (canonical pipeline order)
//   days     — Days at current stage (number, nulls last)
//   rag      — RAG colour (red > amber > green, with unknown last)
//   created_at — implicit "newest" axis; default sort. Not surfaced as
//                a clickable header cell (no Date column on the grid)
//                but available via the default state and the test pin.

const PIPELINE_SORT_STORAGE_KEY = "bonsai_pipeline_sort";

const PIPELINE_DEFAULT_SORT = Object.freeze({
  column: "created_at",
  dir: "desc",
});

// Canonical stage ranking — earlier-in-the-pipeline first.
// Matches the STAGES ordering above so sort-by-stage groups conflict
// rows together, then ob_sent, then ob_returned, …
const PIPELINE_STAGE_ORDER = Object.freeze({
  conflict:    0,
  ob_sent:     1,
  ob_returned: 2,
  forf_draft:  3,
  ccl_draft:   4,
  ccl_signed:  5,
});

// RAG ranking — red is the most-urgent, render-first when sorted desc.
const PIPELINE_RAG_ORDER = Object.freeze({
  red: 0, amber: 1, green: 2, unknown: 3,
});

function _normString(v) {
  return (typeof v === "string" ? v : "").toLocaleLowerCase().trim();
}

function _readPersistedSort() {
  // No silent fallback — if localStorage is unreadable (Office iframe
  // restrictions etc.) or the saved value is malformed, fall back
  // EXPLICITLY to the default. The whole point of this helper is to
  // make that path auditable.
  if (typeof window === "undefined" || !window.localStorage) {
    return PIPELINE_DEFAULT_SORT;
  }
  try {
    const raw = window.localStorage.getItem(PIPELINE_SORT_STORAGE_KEY);
    if (!raw) return PIPELINE_DEFAULT_SORT;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object"
        && typeof parsed.column === "string"
        && (parsed.dir === "asc" || parsed.dir === "desc")) {
      return { column: parsed.column, dir: parsed.dir };
    }
  } catch (_) {
    /* malformed — fall through to default */
  }
  return PIPELINE_DEFAULT_SORT;
}

function _persistSort(next) {
  if (typeof window === "undefined" || !window.localStorage) return;
  try {
    window.localStorage.setItem(
      PIPELINE_SORT_STORAGE_KEY,
      JSON.stringify({ column: next.column, dir: next.dir }),
    );
  } catch (_) {
    /* iframe storage blocked — sort still works in memory; persistence
       is best-effort, not load-bearing. */
  }
}

// Pure comparator — exported via _testHook on window when running under
// jest harness (some repos do, some don't; safe no-op otherwise).
function _compareMatters(a, b, column) {
  // Null/undefined sorts to the BOTTOM. Both null → equal. ONE null →
  // the non-null comes first. Explicit, not a silent default: a missing
  // created_at on a matter should NOT silently rank it above or below
  // the others — it sits at the end so the user can see "this row has
  // no date" rather than getting a random ordering.
  const av = a == null ? null : a[column];
  const bv = b == null ? null : b[column];
  const aMissing = av == null || av === "";
  const bMissing = bv == null || bv === "";
  if (aMissing && bMissing) return 0;
  if (aMissing) return 1;  // a goes after b
  if (bMissing) return -1; // b goes after a

  switch (column) {
    case "created_at":
    case "updated_at": {
      const ta = Date.parse(av);
      const tb = Date.parse(bv);
      // Date.parse returns NaN on garbage — treat as missing (push down).
      if (Number.isNaN(ta) && Number.isNaN(tb)) return 0;
      if (Number.isNaN(ta)) return 1;
      if (Number.isNaN(tb)) return -1;
      return ta - tb;
    }
    case "days":
    case "days_in_stage": {
      const na = Number(av);
      const nb = Number(bv);
      if (Number.isNaN(na) && Number.isNaN(nb)) return 0;
      if (Number.isNaN(na)) return 1;
      if (Number.isNaN(nb)) return -1;
      return na - nb;
    }
    case "stage":
    case "bottleneck_stage": {
      const ra = PIPELINE_STAGE_ORDER[av] ?? 999;
      const rb = PIPELINE_STAGE_ORDER[bv] ?? 999;
      return ra - rb;
    }
    case "rag": {
      const ra = PIPELINE_RAG_ORDER[_normString(av)] ?? 999;
      const rb = PIPELINE_RAG_ORDER[_normString(bv)] ?? 999;
      return ra - rb;
    }
    case "client":
    case "client_name": {
      return _normString(av).localeCompare(_normString(bv));
    }
    case "matter_ref": {
      return _normString(av).localeCompare(_normString(bv));
    }
    case "fee_earner":
    case "fee_earner_name": {
      return _normString(av).localeCompare(_normString(bv));
    }
    default:
      return _normString(av).localeCompare(_normString(bv));
  }
}

function sortMatters(rows, sortState) {
  if (!rows || rows.length === 0) return rows;
  const column = sortState?.column || PIPELINE_DEFAULT_SORT.column;
  const dir = sortState?.dir === "asc" ? "asc" : "desc";
  // Stable sort — slice() to avoid mutating the input array.
  const copy = rows.slice();
  // For client_name on the row shape, the field is `client_name`, but
  // a few callers may pass `client`. Resolve once per column instead
  // of inside every comparator call.
  const resolvedColumn =
    column === "client" ? "client_name"
    : column === "fee_earner" ? "fee_earner_name"
    : column === "stage" ? "bottleneck_stage"
    : column === "days" ? "days_in_stage"
    : column;
  copy.sort((a, b) => {
    const cmp = _compareMatters(a, b, resolvedColumn);
    return dir === "asc" ? cmp : -cmp;
  });
  return copy;
}

// Header cell. Renders as a button so it's keyboard-accessible.
// aria-sort + onClick toggle. Direction default per column:
//   - date/days/rag → "desc" first (newest / most days / most-red first)
//   - text          → "asc"  first (alphabetical)
function PipelineSortHeaderCell({
  label,
  column,
  align = "left",
  activeColumn,
  activeDir,
  onSort,
}) {
  const isActive = activeColumn === column;
  // Per-column natural first-click direction.
  const firstClickDir =
    (column === "created_at" || column === "updated_at"
     || column === "days" || column === "rag")
      ? "desc"
      : "asc";
  const ariaSort = isActive
    ? (activeDir === "asc" ? "ascending" : "descending")
    : "none";
  const arrow = isActive ? (activeDir === "asc" ? " ▲" : " ▼") : "";

  const handleActivate = () => {
    if (isActive) {
      onSort(column, activeDir === "asc" ? "desc" : "asc");
    } else {
      onSort(column, firstClickDir);
    }
  };

  return (
    <div
      role="columnheader"
      aria-sort={ariaSort}
      data-testid={`pipeline-sort-${column}`}
      onClick={handleActivate}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleActivate();
        }
      }}
      tabIndex={0}
      style={{
        cursor: "pointer",
        userSelect: "none",
        textAlign: align,
        // BM-H3775: the row's colour, so the dashboard header can be muted.
        color: isActive ? "#fff" : "inherit",
        outline: "none",
      }}
      onMouseEnter={(e) => { e.currentTarget.style.color = "#fff"; }}
      onMouseLeave={(e) => {
        e.currentTarget.style.color = isActive ? "#fff" : "var(--green)";
      }}
    >
      {label}{arrow}
    </div>
  );
}


// ─── PipelineTrackerCore ─────────────────────────────────────────────────────
export default function PipelineTrackerCore({
  lockedPracticeAreas,
  lockedFeeEarners,
  compact = false,
  showMoneyBar = false,
  showSummaryBar = true,
  showFilters = true,
  maxHeight,
}) {
  const navigate    = useNavigate();
  const _userStr    = localStorage.getItem("user");
  const _userObj    = _userStr ? JSON.parse(_userStr) : null;
  const _userEmail  = (_userObj?.user_email || _userObj?.email || _userObj?.ms_email || sessionStorage.getItem("bonsai_user_email") || localStorage.getItem("bonsai_user_email") || "").toLowerCase().trim();
  const role        = getUserRole(_userEmail);
  const isDirector  = role === ROLES.DIRECTOR || role === ROLES.ADMIN;

  const [matters, setMatters]         = useState([]);
  const [summary, setSummary]         = useState({ total_in_flight: 0, stuck_at_conflict: 0, stuck_at_ob: 0, stuck_at_ccl: 0, ready_to_open: 0 });
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);
  const [stageFilter, setStageFilter] = useState("all");
  const [feeEarnerFilter, setFeeEarnerFilter] = useState("all");
  const [search, setSearch]           = useState("");
  const [lastFetched, setLastFetched] = useState(null);
  const [financialMap, setFinancialMap] = useState({});
  const [creditMap, setCreditMap] = useState({});
  // Bulk-clear of pre-CaseMinder conflict checks (matters with no ref stuck at conflict stage)
  const [selectedCcIds, setSelectedCcIds] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkMsg, setBulkMsg] = useState(null);
  // Track the matter_ref that was just acted on so we can auto-expand it after refetch
  const [autoExpandRef, setAutoExpandRef] = useState(null);

  // Sortable column headers (14 May 2026 — Liezel's "newest first" ask).
  // Hydrate from localStorage so the user's choice survives reloads;
  // initial state is the explicit { created_at, desc } default if
  // nothing is persisted. _readPersistedSort is fail-closed against
  // malformed JSON / blocked storage.
  const [sortState, setSortState] = useState(_readPersistedSort);
  const onSortChange = useCallback((column, dir) => {
    setSortState({ column, dir });
    _persistSort({ column, dir });
  }, []);

  const toggleCcSelection = useCallback((ccId) => {
    setSelectedCcIds((prev) => {
      const next = new Set(prev);
      if (next.has(ccId)) next.delete(ccId); else next.add(ccId);
      return next;
    });
  }, []);

  const fetchPipeline = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/pipeline/summary`, { headers: getHeaders() });
      const adapted = adaptPipelineResponse(res.data);
      setMatters(adapted.matters);
      setSummary(adapted.summary);
      setLastFetched(new Date());

      // Fetch financial + credit data for MoneyBar / CreditChip
      if (showMoneyBar) {
        const allRefs = adapted.matters.map((m) => m.matter_ref).filter(Boolean);
        if (allRefs.length > 0) {
          const refsStr = allRefs.join(",");
          // PR Q (21 May 2026): no more silent fallback. Vanessa
          // reported the per-matter MoneyBar had "gone" — root cause
            // was this catch swallowing fetch failures, leaving
          // financialMap empty so every row rendered the 6px
          // compact-empty placeholder which is visually invisible.
          // Now: failures surface via window.errorMessage AND the
          // bar still renders, so at least the structure is visible.
          // Credit-control is still treated as best-effort because it
          // is a secondary chip, not the primary money signal.
          try {
            const [finRes, creditRes] = await Promise.all([
              axios.get(`${API}/money-on-account/financial-summary/batch`, {
                params: { refs: refsStr },
                headers: getHeaders(),
              }),
              axios.get(`${API}/credit-control/batch-summary`, {
                params: { refs: refsStr },
                headers: getHeaders(),
              }).catch((ce) => {
                if (typeof window !== "undefined" && typeof window.errorMessage === "function") {
                  try { window.errorMessage(ce, "Credit-control batch summary failed (chip suppressed)"); } catch (_) { /* ignore */ }
                }
                return { data: [] };
              }),
            ]);
            const fMap = {};
            (finRes.data || []).forEach((s) => { fMap[s.matter_reference] = s; });
            setFinancialMap(fMap);
            const cMap = {};
            (creditRes.data || []).forEach((s) => { cMap[s.matter_ref] = s; });
            setCreditMap(cMap);
          } catch (fe) {
            // Loud failure: tell the user, do NOT swallow.
            if (typeof window !== "undefined" && typeof window.errorMessage === "function") {
              try {
                window.errorMessage(
                  fe,
                  "MoneyBar data fetch failed \u2014 per-matter bars will show empty until this resolves",
                );
              } catch (_) { /* never throw from error handler */ }
            }
            // Reset both maps so the UI reflects the true state (no
            // stale data from a previous successful fetch).
            setFinancialMap({});
            setCreditMap({});
          }
        }
      }
    } catch (e) {
      // BM-H164 (28 Aug 2026): this was the one fetchPipeline call site
      // never migrated to _errorMessage during the BM-H161 sweep. A
      // dict-shaped 500 detail (global_exception_handler's
      // {error, message, endpoint, trace} shape) was landing straight
      // into `error` state and rendering as a raw object behind the
      // error boundary — "Failed to load pipeline" for every user, with
      // /pipeline/summary itself perfectly healthy underneath.
      setError(_errorMessage(e, "Failed to load pipeline"));
    } finally {
      setLoading(false);
    }
  }, [showMoneyBar]);

  useEffect(() => { fetchPipeline(); }, [fetchPipeline]);

  // WP-J: Reference Fixer writes a timestamp to sessionStorage after a
  // successful rename confirm.  If that timestamp is newer than our last
  // fetch, refetch immediately so the pipeline list reflects the new ref.
  // BM-H525: extracted into a shared hook so MatterDetail.jsx (and any
  // future page) can reuse the exact same listener — see
  // frontend/src/hooks/usePipelineCacheInvalidation.js. Behaviour here is
  // unchanged.
  usePipelineCacheInvalidation(fetchPipeline);

  // Wraps fetchPipeline to auto-expand a specific matter after refetch (used as onSuccess)
  const onActionSuccess = useCallback(async (matterRef) => {
    if (matterRef) setAutoExpandRef(matterRef);
    await fetchPipeline();
  }, [fetchPipeline]);

  const handleBulkClearPreCaseminder = useCallback(async () => {
    const ids = Array.from(selectedCcIds);
    if (ids.length === 0) return;
    if (!_userEmail) {
      setBulkMsg({ kind: "error", text: "Cannot identify current user — please sign in again." });
      return;
    }
    const ok = window.confirm(
      `Mark ${ids.length} conflict check${ids.length === 1 ? "" : "s"} as pre-CaseMinder reviewed?\n\n` +
      `These matters will be cleared from the conflict_check stage with an audit note recording your approval.`
    );
    if (!ok) return;
    setBulkBusy(true);
    setBulkMsg(null);
    try {
      const res = await axios.post(
        `${API}/conflict-checks/bulk-clear-pre-caseminder`,
        { conflict_ids: ids, approved_by: _userEmail, reason: "Pre-CaseMinder matter — conflict review done outside CaseMinder" },
        { headers: getHeaders() }
      );
      const s = res.data?.summary || {};
      setBulkMsg({
        kind: "success",
        text: `Cleared ${s.cleared || 0}${s.already_cleared ? `, ${s.already_cleared} already cleared` : ""}${s.not_found ? `, ${s.not_found} not found` : ""}${s.errors ? `, ${s.errors} errors` : ""}.`,
      });
      setSelectedCcIds(new Set());
      await fetchPipeline();
    } catch (e) {
      setBulkMsg({ kind: "error", text: errorMessage(e, "Bulk clear failed") });
    } finally {
      setBulkBusy(false);
    }
  }, [selectedCcIds, _userEmail, fetchPipeline]);

  // Item 21-patch (#189): free-text search is resolved server-side via
  // /api/smart-search (search_resolver.resolve) instead of a client-side
  // substring match — "one resolver, everywhere" per
  // backend/hub/SEARCH_RESOLVER.md. The resolver has no tracker-type scope
  // param, so the pipeline/stage/fee-earner narrowing below stays
  // client-side on top of the resolved matter set (matches SEARCH_RESOLVER.md
  // guidance: don't expand the backend schema, narrow locally instead).
  //
  // BM-H3451 (2 Oct 2026): searching "1539" found nothing while "ash
  // scaffolding" found BL.VEC.002.1539. /smart-search defaults to
  // active_only=true, which excludes pending matters, and every matter here
  // is pre-open. Names still surfaced through the fuzzy name layer, which
  // has no status filter; a number has no name to fuzzy-match. So: ask for
  // pending matters too, widen the cap past 8, and match references
  // directly on the rows already loaded.
  const { matchedRefs, matchedClientNames } = useSmartSearchResolver(search, {
    activeOnly: false,
    limit: 50,
  });

  // Apply locked filters + user filters
  const filtered = matters.filter((m) => {
    // Locked fee earner filter (compare initials — API returns full names)
    // Run fee earner check FIRST — this is the primary identity filter
    if (lockedFeeEarners && lockedFeeEarners.length > 0) {
      const feInitials = NAME_TO_INITIALS[(m.fee_earner_name || "").toLowerCase()] || m.fee_earner_name;
      const mattRef = (m.matter_ref || "").toUpperCase();
      const matchesFE = lockedFeeEarners.includes(feInitials) ||
        lockedFeeEarners.some(ini => mattRef.includes(`.${ini}.`));
      if (!matchesFE) return false;
    }
    // Locked practice area filter — only exclude if we CAN determine the area
    // and it doesn't match. Matters with unknown area still pass if fee earner matched.
    if (lockedPracticeAreas && lockedPracticeAreas.length > 0) {
      const pa = matterTypeToPracticeArea(m.matter_type, m.matter_ref);
      if (pa && !lockedPracticeAreas.includes(pa)) return false;
    }
    // User stage filter
    if (stageFilter !== "all" && m.bottleneck_stage !== stageFilter) return false;
    // User fee earner filter (only when not locked)
    if (!lockedFeeEarners && feeEarnerFilter !== "all" && m.fee_earner_name !== feeEarnerFilter) return false;
    // Search — resolved via /api/smart-search (see useSmartSearchResolver).
    // Empty search box: show full list as before (untouched behaviour).
    if (search && search.trim().length >= 2) {
      const ref = (m.matter_ref || "").toUpperCase();
      const clientLower = (m.client_display_name || m.client_name || "").toLowerCase();
      return (
        matchedRefs.has(ref) ||
        matchedClientNames.has(clientLower) ||
        refMatchesQuery(m.matter_ref, search)
      );
    }
    return true;
  });

  const feeEarners = [...new Set(matters.map((m) => m.fee_earner_name).filter(Boolean))].sort();

  // Sortable column headers (14 May 2026 — Liezel's ask). Memoised on
  // (filtered, sortState) so re-renders that don't change either skip
  // the sort. sortMatters is pure and returns a fresh array so the
  // PipelineRow keys still work.
  const sorted = useMemo(
    () => sortMatters(filtered, sortState),
    [filtered, sortState],
  );

  // Determine if filters should be shown
  const bothLocked = lockedPracticeAreas && lockedFeeEarners;
  const effectiveShowFilters = showFilters && !bothLocked;

  // ── Compact mode (dashboard embed) ──────────────────────────────────────
  if (compact) {
    return (
      <div
        className="bg-navy-card border border-navy-border rounded-lg overflow-hidden mb-6"
        data-testid="pipeline-tracker-core-compact"
      >
        {/* Header */}
        <div className="px-4 py-3 border-b border-navy-border flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold text-white">Pipeline</h3>
            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-teal/40 text-teal font-bold">
              {filtered.length}
            </span>
          </div>
          <Link
            to="/pipeline"
            className="text-[10px] text-teal hover:text-teal flex items-center gap-1 transition-colors"
          >
            View all <span style={{ fontSize: 10 }}>→</span>
          </Link>
        </div>

        <div style={maxHeight ? { maxHeight, overflowY: "auto" } : undefined}>
          {loading && matters.length === 0 ? (
            <div style={{ textAlign: "center", padding: 40, color: "var(--text-faint)", fontSize: 13 }}>Loading pipeline…</div>
          ) : error ? (
            <div style={{ textAlign: "center", padding: 20, color: "var(--red)", fontSize: 12 }}>⚠ {error}</div>
          ) : filtered.length === 0 ? (
            <div style={{ textAlign: "center", padding: 40, color: "var(--text-faint)", fontSize: 13 }}>No matters match the current filters.</div>
          ) : (
            <div>
              {/* col headers — sortable (14 May 2026, Liezel's ask) */}
              <div
                role="row"
                data-testid="pipeline-header-row-compact"
                style={{
                  display: "grid",
                  gridTemplateColumns: "24px minmax(160px,2fr) 120px 1fr 64px 60px 44px",
                  gap: 10, padding: "8px 16px",
                  background: "#071020",
                  borderBottom: "1px solid var(--navy-border)",
                  fontSize: 10, fontWeight: 700, color: "var(--text-muted)",
                  letterSpacing: "0.08em", textTransform: "uppercase",
                }}
              >
                <div />
                <PipelineSortHeaderCell
                  label="Client / Ref"
                  column="client"
                  activeColumn={sortState.column}
                  activeDir={sortState.dir}
                  onSort={onSortChange}
                />
                <PipelineSortHeaderCell
                  label="Fee Earner"
                  column="fee_earner"
                  activeColumn={sortState.column}
                  activeDir={sortState.dir}
                  onSort={onSortChange}
                />
                <PipelineSortHeaderCell
                  label="Stages"
                  column="stage"
                  activeColumn={sortState.column}
                  activeDir={sortState.dir}
                  onSort={onSortChange}
                />
                <PipelineSortHeaderCell
                  label="Days"
                  column="days"
                  align="center"
                  activeColumn={sortState.column}
                  activeDir={sortState.dir}
                  onSort={onSortChange}
                />
                <PipelineSortHeaderCell
                  label="RAG"
                  column="rag"
                  activeColumn={sortState.column}
                  activeDir={sortState.dir}
                  onSort={onSortChange}
                />
                <div />
              </div>
              {sorted.map((m, i) => (
                <PipelineRow
                  key={`${m.matter_ref || 'no-ref'}-${m.conflict_check_id || 'no-cc'}-${i}`}
                  matter={m}
                  navigate={navigate}
                  onSuccess={onActionSuccess}
                  isDirector={isDirector}
                  showMoneyBar={showMoneyBar}
                  financials={financialMap[m.matter_ref]}
                  credit={creditMap[m.matter_ref]}
                  selectedCcIds={selectedCcIds}
                  onToggleCcSelection={toggleCcSelection}
                  autoExpand={autoExpandRef && m.matter_ref === autoExpandRef}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── Full mode (standalone page content) ─────────────────────────────────
  return (
    <div>
      {/* Refresh / timestamp row */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", marginBottom: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 12, color: "var(--text-faint)" }}>
            {lastFetched ? `Updated ${lastFetched.toLocaleTimeString("en-GB")}` : "Loading…"}
          </span>
          <button
            onClick={fetchPipeline}
            disabled={loading}
            style={{ ...ghostBtnStyle, display: "flex", alignItems: "center", gap: 6, fontSize: 12, opacity: loading ? 0.5 : 1 }}
          >
            {loading ? "⏳" : "↻"} Refresh
          </button>
        </div>
      </div>

      {showSummaryBar && (
        <SummaryBar summary={summary} stageFilter={stageFilter} setStageFilter={setStageFilter} />
      )}

      {effectiveShowFilters && (
        <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search client, ref, type…"
            style={{
              flex: "1 1 220px", background: "var(--navy)", border: "1px solid var(--navy-border)",
              borderRadius: 6, color: "var(--text)", fontSize: 12, padding: "7px 12px", outline: "none",
            }}
          />
          {!lockedFeeEarners && (
            <select
              value={feeEarnerFilter}
              onChange={(e) => setFeeEarnerFilter(e.target.value)}
              style={{ background: "var(--navy)", border: "1px solid var(--navy-border)", borderRadius: 6, color: "var(--text)", fontSize: 12, padding: "7px 12px", outline: "none" }}
            >
              <option value="all">All fee earners</option>
              {feeEarners.map((fe) => <option key={fe} value={fe}>{fe}</option>)}
            </select>
          )}
          {(stageFilter !== "all" || feeEarnerFilter !== "all" || search) && (
            <FilterChip
              label={[stageFilter !== "all" && stageFilter, feeEarnerFilter !== "all" && feeEarnerFilter, search && `"${search}"`].filter(Boolean).join(" · ")}
              onRemove={() => { setStageFilter("all"); setFeeEarnerFilter("all"); setSearch(""); }}
            />
          )}
          <div style={{ marginLeft: "auto", fontSize: 11, color: "var(--text-faint)" }}>{filtered.length} matters</div>
        </div>
      )}

      {error ? (
        <div style={{ background: "#1a0606", border: "1px solid #ef4444", borderRadius: 8, padding: "16px 20px", color: "var(--red)", fontSize: 13 }}>
          ⚠ {error}
        </div>
      ) : loading && matters.length === 0 ? (
        <div style={{ textAlign: "center", padding: 60, color: "var(--text-faint)", fontSize: 14 }}>Loading pipeline…</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: "center", padding: 60, color: "var(--text-faint)", fontSize: 14 }}>No matters match the current filters.</div>
      ) : (
        <div style={{ border: "1px solid var(--navy-border)", borderRadius: 8, overflow: "hidden" }}>
          {/* col headers — sortable (14 May 2026, Liezel's ask) */}
          <div
            role="row"
            data-testid="pipeline-header-row"
            style={{
              display: "grid",
              gridTemplateColumns: "24px minmax(160px,2fr) 120px 1fr 64px 60px 44px",
              gap: 10, padding: "8px 16px",
              background: "#071020",
              borderBottom: "1px solid var(--navy-border)",
              fontSize: 10, fontWeight: 700, color: "var(--text-muted)",
              letterSpacing: "0.08em", textTransform: "uppercase",
            }}
          >
            <div />
            <PipelineSortHeaderCell
              label="Client / Ref"
              column="client"
              activeColumn={sortState.column}
              activeDir={sortState.dir}
              onSort={onSortChange}
            />
            <PipelineSortHeaderCell
              label="Fee Earner"
              column="fee_earner"
              activeColumn={sortState.column}
              activeDir={sortState.dir}
              onSort={onSortChange}
            />
            <PipelineSortHeaderCell
              label="Stages"
              column="stage"
              activeColumn={sortState.column}
              activeDir={sortState.dir}
              onSort={onSortChange}
            />
            <PipelineSortHeaderCell
              label="Days"
              column="days"
              align="center"
              activeColumn={sortState.column}
              activeDir={sortState.dir}
              onSort={onSortChange}
            />
            <PipelineSortHeaderCell
              label="RAG"
              column="rag"
              activeColumn={sortState.column}
              activeDir={sortState.dir}
              onSort={onSortChange}
            />
            <div />
          </div>
          {sorted.map((m, i) => (
            <PipelineRow
              key={`${m.matter_ref || 'no-ref'}-${m.conflict_check_id || 'no-cc'}-${i}`}
              matter={m}
              navigate={navigate}
              onSuccess={onActionSuccess}
              isDirector={isDirector}
              showMoneyBar={showMoneyBar}
              financials={financialMap[m.matter_ref]}
              credit={creditMap[m.matter_ref]}
              selectedCcIds={selectedCcIds}
              onToggleCcSelection={toggleCcSelection}
              autoExpand={autoExpandRef && m.matter_ref === autoExpandRef}
            />
          ))}
        </div>
      )}

      {/* Floating action bar for bulk pre-CaseMinder clear */}
      {selectedCcIds.size > 0 && (
        <div style={{
          position: "fixed",
          bottom: 24,
          left: "50%",
          transform: "translateX(-50%)",
          background: "#0c1a28",
          border: "1px solid var(--amber)",
          borderRadius: 10,
          padding: "12px 18px",
          boxShadow: "0 8px 28px rgba(0,0,0,0.6)",
          zIndex: 100,
          display: "flex",
          alignItems: "center",
          gap: 14,
          fontSize: 13,
        }}>
          <span style={{ color: "var(--text)" }}>
            <strong style={{ color: "var(--amber)" }}>{selectedCcIds.size}</strong> selected
          </span>
          <button
            onClick={handleBulkClearPreCaseminder}
            disabled={bulkBusy}
            style={{
              background: bulkBusy ? "#333" : "var(--amber)",
              color: "#000",
              border: "none",
              borderRadius: 6,
              padding: "7px 14px",
              fontWeight: 700,
              cursor: bulkBusy ? "not-allowed" : "pointer",
              fontSize: 12,
              opacity: bulkBusy ? 0.6 : 1,
            }}
          >
            {bulkBusy ? "Clearing…" : `Mark ${selectedCcIds.size} as pre-CaseMinder reviewed`}
          </button>
          <button
            onClick={() => setSelectedCcIds(new Set())}
            disabled={bulkBusy}
            style={{ ...ghostBtnStyle, fontSize: 12 }}
          >
            Cancel
          </button>
        </div>
      )}

      {bulkMsg && (
        <div style={{
          position: "fixed",
          bottom: selectedCcIds.size > 0 ? 88 : 24,
          left: "50%",
          transform: "translateX(-50%)",
          background: bulkMsg.kind === "success" ? "#062815" : "#1a0606",
          border: `1px solid ${bulkMsg.kind === "success" ? "var(--green)" : "var(--red)"}`,
          color: bulkMsg.kind === "success" ? "var(--green)" : "var(--red)",
          borderRadius: 8,
          padding: "10px 16px",
          zIndex: 99,
          fontSize: 12,
          maxWidth: 600,
        }}>
          {bulkMsg.kind === "success" ? "\u2713 " : "\u26a0 "}{bulkMsg.text}
          <button onClick={() => setBulkMsg(null)} style={{ marginLeft: 12, background: "transparent", border: "none", color: "inherit", cursor: "pointer", fontSize: 14 }}>×</button>
        </div>
      )}
    </div>
  );
}
